/**
 * CoreDashboard — StudoX Core Admin & Teacher Panel
 * ─────────────────────────────────────────────────────────────────────────────
 * Features:
 *   1. Code-Generator   — Bulk code generation, CSV export, QR print view
 *   2. User-Management  — List/search/deactivate all users + student profiles
 *   4. Institutionen    — Multi-school management (admin only)
 *   5. Lehrer-Panel     — Teacher restricted view, institution linking
 *   6. Klassen-Builder  — Class CRUD + student member management
 */

import { useState, useEffect, useCallback } from "react";
import { useAuthStore } from "@/store/authStore";
import CreatorStudioPanel from "./CreatorStudioPanel";
import { creatableRanks, rankMeta } from "@/lib/studox-auth/index.js";
import { buildInviteLink } from "@/lib/studox-auth/invite.js";

const API = import.meta.env.VITE_API_URL || "http://localhost:4000/api";

/* ── the full palette of grantable apps (shared by CodeGen + per-child editor) ── */
export const APP_CATALOG = [
  { id: "flow",   label: "Flow",   color: "#8b5cf6", icon: "🎓" },
  { id: "meet",   label: "Meet",   color: "#7c3aed", icon: "🎧" },
  { id: "clubs",  label: "Clubs",  color: "#10b981", icon: "🏛️" },
  { id: "create", label: "Create", color: "#f43f5e", icon: "🎨" },
  { id: "cinema", label: "Cinema", color: "#f59e0b", icon: "🎬" },
  { id: "quest",  label: "Quest",  color: "#22c55e", icon: "🧭" },
  { id: "music",  label: "Music",  color: "#06b6d4", icon: "🎵" },
  { id: "mic",    label: "Mic",    color: "#ec4899", icon: "🎤" },
  { id: "lyrics", label: "Lyrics", color: "#a855f7", icon: "✍️" },
  { id: "events", label: "Events", color: "#fbbf24", icon: "📅" },
  { id: "step",   label: "Step",   color: "#f97316", icon: "🪜" },
];
const APP_CATALOG_IDS = APP_CATALOG.map((a) => a.id);

/* ── shared fetch helper ────────────────────────────────────────────────────── */
async function apiFetch(path, opts = {}) {
  const token = useAuthStore.getState().token;
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

/* ── Verified Badge ─────────────────────────────────────────────────────────── */
function VerifiedBadge({ role, size = 16, className = "" }) {
  const isSolidGround = role === "solid_ground";
  const color = isSolidGround ? "#fbbf24" : "#3b82f6";
  const glow  = isSolidGround ? "rgba(251,191,36,0.6)" : "rgba(59,130,246,0.6)";
  return (
    <span
      title={isSolidGround ? "Solid Ground Admin — Verified Platform Owner" : "Verifiziert"}
      className={`inline-flex items-center justify-center flex-shrink-0 ${className}`}
      style={{ filter: `drop-shadow(0 0 4px ${glow})` }}
    >
      <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
      </svg>
    </span>
  );
}

/* ── tiny UI atoms ──────────────────────────────────────────────────────────── */
function Pill({ children, color = "#8b5cf6" }) {
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold"
      style={{ background: `${color}18`, border: `1px solid ${color}30`, color }}
    >
      {children}
    </span>
  );
}

function Btn({ children, onClick, disabled, variant = "primary", size = "md", className = "" }) {
  const base = "inline-flex items-center justify-center gap-1.5 font-black rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:scale-100";
  const sizes = { sm: "px-3 py-1.5 text-xs", md: "px-4 py-2 text-sm", lg: "px-5 py-2.5 text-sm" };
  const variants = {
    primary: { background: "linear-gradient(135deg,#8b5cf6,#06b6d4)", color: "#fff", boxShadow: "0 0 16px rgba(139,92,246,0.3)" },
    danger:  { background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)", color: "#f87171" },
    ghost:   { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "#9ca3af" },
    success: { background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.3)", color: "#34d399" },
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${base} ${sizes[size]} ${className}`}
      style={variants[variant]}
    >
      {children}
    </button>
  );
}

function Input({ label, value, onChange, placeholder, type = "text", icon }) {
  return (
    <div>
      {label && <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1.5 ml-1">{label}</label>}
      <div className="relative">
        {icon && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 text-sm">{icon}</span>}
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="w-full py-2.5 rounded-xl text-sm text-white placeholder-gray-600 outline-none transition-all"
          style={{
            paddingLeft: icon ? "2.25rem" : "0.875rem",
            paddingRight: "0.875rem",
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(139,92,246,0.18)",
          }}
          onFocus={(e) => { e.target.style.borderColor = "rgba(139,92,246,0.5)"; e.target.style.background = "rgba(139,92,246,0.06)"; }}
          onBlur={(e)  => { e.target.style.borderColor = "rgba(139,92,246,0.18)"; e.target.style.background = "rgba(255,255,255,0.04)"; }}
        />
      </div>
    </div>
  );
}

function Select({ label, value, onChange, options, placeholder }) {
  return (
    <div>
      {label && <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1.5 ml-1">{label}</label>}
      <select
        value={value}
        onChange={onChange}
        className="w-full px-3 py-2.5 rounded-xl text-sm text-white outline-none transition-all appearance-none"
        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(139,92,246,0.18)" }}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value} style={{ background: "#0d0d1e" }}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

function Card({ children, accent = "#8b5cf6", className = "" }) {
  return (
    <div
      className={`rounded-2xl border p-4 ${className}`}
      style={{ background: `${accent}08`, borderColor: `${accent}20` }}
    >
      {children}
    </div>
  );
}

function StatCard({ label, value, icon, color = "#8b5cf6", sub }) {
  return (
    <Card accent={color} className="flex items-center gap-4">
      <div className="text-2xl w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0"
        style={{ background: `${color}15` }}>{icon}</div>
      <div>
        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-600">{label}</p>
        <p className="text-2xl font-black text-white">{value}</p>
        {sub && <p className="text-[10px] text-gray-600">{sub}</p>}
      </div>
    </Card>
  );
}

function Toast({ msg, type = "success", onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 3200); return () => clearTimeout(t); }, [onDone]);
  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] flex items-center gap-3 px-5 py-3 rounded-2xl border text-sm font-bold"
      style={{
        background: "rgba(8,8,24,0.97)",
        borderColor: type === "success" ? "rgba(52,211,153,0.4)" : "rgba(239,68,68,0.4)",
        color: type === "success" ? "#34d399" : "#f87171",
        boxShadow: `0 0 30px ${type === "success" ? "rgba(52,211,153,0.2)" : "rgba(239,68,68,0.2)"}`,
        backdropFilter: "blur(20px)",
        animation: "toastIn 0.4s cubic-bezier(0.34,1.56,0.64,1) forwards",
      }}>
      {type === "success" ? "✅" : "❌"} {msg}
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex justify-center items-center py-12">
      <svg className="animate-spin w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2.5">
        <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
      </svg>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   PANEL 1: CODE-GENERATOR
   ───────────────────────────────────────────────────────────────────────────── */
function CodeGenPanel({ institutions, userRole }) {
  const [form, setForm] = useState({
    role: "student",
    count: 1,
    note: "",
    expiresAt: "",
    institutionId: "",
    apps: ["flow"],
  });
  const [loading, setLoading]   = useState(false);
  const [codes, setCodes]       = useState([]);
  const [error, setError]       = useState("");
  const [showPrint, setShowPrint] = useState(false);
  const [toast, setToast]       = useState(null);

  const toggleApp = (app) => {
    setForm((f) => ({
      ...f,
      apps: f.apps.includes(app) ? f.apps.filter((a) => a !== app) : [...f.apps, app],
    }));
  };

  const generate = async () => {
    if (form.apps.length === 0) { setError("Mindestens eine App auswählen"); return; }
    setLoading(true);
    setError("");
    try {
      const data = await apiFetch("/codes/generate", {
        method: "POST",
        body: JSON.stringify({
          role: form.role,
          count: parseInt(form.count),
          note: form.note || null,
          expiresAt: form.expiresAt || null,
          institutionId: form.institutionId || null,
          allowedApps: form.apps,
        }),
      });
      setCodes(data.codes || [data.code]);
      setToast({ msg: `${data.codes?.length || 1} Code(s) erfolgreich generiert!`, type: "success" });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyAll = () => {
    navigator.clipboard?.writeText(codes.map((c) => c.code).join("\n"));
    setToast({ msg: "Alle Codes kopiert!", type: "success" });
  };

  const downloadCSV = () => {
    const header = "Code,Rolle,Apps,Ablaufdatum,Institution,Erstellt";
    const rows = codes.map((c) =>
      `${c.code},${c.role},${c.allowedApps},${c.expiresAt || ""},${c.institutionName || ""},${new Date(c.createdAt).toLocaleDateString("de-DE")}`
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `studox-codes-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setToast({ msg: "CSV heruntergeladen!", type: "success" });
  };

  const printView = () => {
    const win = window.open("", "_blank");
    const rows = codes.map((c) => `
      <tr>
        <td style="padding:12px 16px;font-family:monospace;font-size:18px;font-weight:900;letter-spacing:2px;border-bottom:1px solid #e5e7eb">${c.code}</td>
        <td style="padding:12px 16px;color:#6b7280;border-bottom:1px solid #e5e7eb">${c.role}</td>
        <td style="padding:12px 16px;color:#6b7280;border-bottom:1px solid #e5e7eb">${c.allowedApps}</td>
        <td style="padding:12px 16px;color:#6b7280;border-bottom:1px solid #e5e7eb">${c.expiresAt ? new Date(c.expiresAt).toLocaleDateString("de-DE") : "Kein Ablauf"}</td>
      </tr>`).join("");
    win.document.write(`<!DOCTYPE html><html><head><title>StudoX Codes</title><style>
      body{font-family:Inter,system-ui,sans-serif;padding:32px;color:#111}
      h1{font-size:24px;font-weight:900;margin-bottom:4px}p{color:#6b7280;margin-bottom:24px}
      table{width:100%;border-collapse:collapse}
      th{text-align:left;padding:8px 16px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#9ca3af;border-bottom:2px solid #e5e7eb}
      @media print{button{display:none}}
    </style></head><body>
    <h1>StudoX Codes</h1>
    <p>Generiert am ${new Date().toLocaleDateString("de-DE")} — ${codes.length} Code(s)</p>
    <button onclick="window.print()" style="margin-bottom:24px;padding:8px 20px;background:#8b5cf6;color:#fff;border:none;border-radius:8px;font-weight:700;cursor:pointer">🖨️ Drucken</button>
    <table><thead><tr><th>StudoX-Code</th><th>Rolle</th><th>Apps</th><th>Ablaufdatum</th></tr></thead><tbody>${rows}</tbody></table>
    </body></html>`);
    win.document.close();
  };

  const APPS = APP_CATALOG;
  const ALL_APP_IDS = APP_CATALOG_IDS;

  // Escalation-safe: only roles this actor's rank is actually allowed to create
  // (server-enforced too via canCreateRank — this just keeps the UI honest).
  const ROLES = creatableRanks(userRole).map((id) => ({ value: id, label: rankMeta(id).title }));

  return (
    <div className="space-y-6">
      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}

      <div>
        <h2 className="text-xl font-black text-white mb-1">Einladungen</h2>
        <p className="text-sm text-gray-600">Codes &amp; Einladungslinks für ganze Klassen — mit Ablaufdatum, App-Zugang, CSV-Export und Rang-Eskalationsschutz.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Select
          label="Rolle"
          value={form.role}
          onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
          options={ROLES}
        />
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1.5 ml-1">Anzahl (1–50)</label>
          <input
            type="number" min={1} max={50}
            value={form.count}
            onChange={(e) => setForm((f) => ({ ...f, count: Math.max(1, Math.min(50, parseInt(e.target.value) || 1)) }))}
            className="w-full px-3 py-2.5 rounded-xl text-sm text-white outline-none"
            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(139,92,246,0.18)" }}
          />
        </div>
        <Input
          label="Ablaufdatum (optional)"
          type="date"
          value={form.expiresAt}
          onChange={(e) => setForm((f) => ({ ...f, expiresAt: e.target.value }))}
        />
        <Select
          label="Institution (optional)"
          value={form.institutionId}
          onChange={(e) => setForm((f) => ({ ...f, institutionId: e.target.value }))}
          placeholder="Keine Institution"
          options={institutions.map((i) => ({ value: i.id, label: i.name }))}
        />
        <div className="sm:col-span-2">
          <Input
            label="Notiz (optional)"
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            placeholder="z.B. Klasse 4B, Schuljahr 2025/26"
            icon="📝"
          />
        </div>
      </div>

      {/* App-Zugang */}
      <div>
        <div className="flex items-center justify-between mb-2 ml-1">
          <p className="text-[10px] font-bold uppercase tracking-widest text-gray-600">App-Zugang — pro App einzeln freigeben</p>
          <div className="flex gap-1.5">
            <button onClick={() => setForm((f) => ({ ...f, apps: [...ALL_APP_IDS] }))}
              className="px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all hover:scale-105"
              style={{ background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.3)", color: "#4ade80" }}>
              ✓ Alle
            </button>
            <button onClick={() => setForm((f) => ({ ...f, apps: ["flow"] }))}
              className="px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all hover:scale-105"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", color: "#9ca3af" }}>
              Nur Flow
            </button>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {APPS.map((app) => {
            const active = form.apps.includes(app.id);
            return (
              <button
                key={app.id}
                onClick={() => toggleApp(app.id)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-bold transition-all"
                style={{
                  background: active ? `${app.color}18` : "rgba(255,255,255,0.03)",
                  borderColor: active ? `${app.color}50` : "rgba(255,255,255,0.08)",
                  color: active ? app.color : "#6b7280",
                  boxShadow: active ? `0 0 14px ${app.color}20` : "none",
                }}
              >
                {app.icon} {app.label}
                {active && <span style={{ color: app.color, fontSize: 10 }}>✓</span>}
              </button>
            );
          })}
        </div>
      </div>

      {error && (
        <div className="px-4 py-2.5 rounded-xl text-xs text-red-400" style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)" }}>
          {error}
        </div>
      )}

      <Btn onClick={generate} disabled={loading} size="lg">
        {loading ? <><svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg> Generiere…</> : `🔑 ${form.count} Code${form.count > 1 ? "s" : ""} generieren`}
      </Btn>

      {/* Results */}
      {codes.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold text-gray-500">{codes.length} Code{codes.length > 1 ? "s" : ""} generiert</p>
            <div className="flex gap-2">
              <Btn variant="ghost" size="sm" onClick={copyAll}>📋 Alle kopieren</Btn>
              <Btn variant="ghost" size="sm" onClick={downloadCSV}>⬇️ CSV</Btn>
              <Btn variant="ghost" size="sm" onClick={printView}>🖨️ Drucken</Btn>
            </div>
          </div>

          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
            {codes.map((c) => (
              <div key={c.id}
                className="flex items-center justify-between px-4 py-3 rounded-xl border"
                style={{ background: "rgba(139,92,246,0.06)", borderColor: "rgba(139,92,246,0.2)" }}>
                <div>
                  <p className="font-mono text-lg font-black tracking-widest text-white">{c.code}</p>
                  <p className="text-[10px] text-gray-600 mt-0.5">
                    {c.role} · {JSON.parse(c.allowedApps || '["flow"]').join(", ")}
                    {c.expiresAt && ` · bis ${new Date(c.expiresAt).toLocaleDateString("de-DE")}`}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => { navigator.clipboard?.writeText(buildInviteLink("https://core.studox.eu", c.code)); setToast({ msg: "Einladungslink kopiert!", type: "success" }); }}
                    className="text-gray-600 hover:text-cyan-400 transition-colors text-lg"
                    title="Einladungslink kopieren"
                  >🔗</button>
                  <button
                    onClick={() => { navigator.clipboard?.writeText(c.code); setToast({ msg: "Code kopiert!", type: "success" }); }}
                    className="text-gray-600 hover:text-violet-400 transition-colors text-lg"
                    title="Nur Code kopieren"
                  >📋</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Per-child app access editor ─────────────────────────────────── */}
      <AppAccessEditor onToast={setToast} />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Per-child app-access editor — retune EXACTLY which apps an existing kid may
   enter, after registration. Reads the child's current allowedApps, lets an
   admin toggle each app, saves via PATCH /admin/users/:id/apps (audited).
   ───────────────────────────────────────────────────────────────────────────── */
function AppAccessEditor({ onToast }) {
  const [target, setTarget] = useState(null);
  const [apps, setApps] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  const pick = (u) => {
    setTarget(u);
    setDirty(false);
    if (!u) { setApps([]); return; }
    let cur = ["flow"];
    try { cur = JSON.parse(u.allowedApps || '["flow"]'); } catch {}
    setApps(Array.isArray(cur) ? cur.filter((a) => APP_CATALOG_IDS.includes(a)) : ["flow"]);
  };
  const toggle = (id) => {
    setDirty(true);
    setApps((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));
  };
  const save = async () => {
    if (!target) return;
    setBusy(true);
    try {
      await apiFetch(`/admin/users/${target.id}/apps`, { method: "PATCH", body: JSON.stringify({ allowedApps: apps }) });
      onToast?.({ msg: `App-Zugang für ${target.name} gespeichert`, type: "success" });
      setDirty(false);
    } catch (e) { onToast?.({ msg: e.message || "Speichern fehlgeschlagen", type: "error" }); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border p-5 mt-2" style={{ background: "rgba(124,58,237,0.05)", borderColor: "rgba(124,58,237,0.22)" }}>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-lg">🔐</span>
        <h3 className="text-base font-black text-white">App-Zugang eines Kindes anpassen</h3>
      </div>
      <p className="text-xs text-gray-500 mb-4">Suche ein bestehendes Kind und gib jede App einzeln frei oder sperre sie — sofort wirksam, vollständig auditiert.</p>

      <UserPicker value={target} onSelect={pick} />

      {target && (
        <div className="mt-4 animate-fade-in">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-600">Freigegebene Apps für <span className="text-violet-300">{target.name}</span></p>
            <div className="flex gap-1.5">
              <button onClick={() => { setApps([...APP_CATALOG_IDS]); setDirty(true); }} className="px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider" style={{ background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.3)", color: "#4ade80" }}>✓ Alle</button>
              <button onClick={() => { setApps(["flow"]); setDirty(true); }} className="px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", color: "#9ca3af" }}>Nur Flow</button>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {APP_CATALOG.map((app) => {
              const active = apps.includes(app.id);
              return (
                <button key={app.id} onClick={() => toggle(app.id)}
                  className="flex items-center gap-2 px-3.5 py-2 rounded-xl border text-sm font-bold transition-all hover:scale-[1.03]"
                  style={{ background: active ? `${app.color}18` : "rgba(255,255,255,0.03)", borderColor: active ? `${app.color}50` : "rgba(255,255,255,0.08)", color: active ? app.color : "#6b7280", boxShadow: active ? `0 0 14px ${app.color}20` : "none" }}>
                  <span>{app.icon}</span>{app.label}
                  {active && <span className="text-[10px]">✓</span>}
                </button>
              );
            })}
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Btn onClick={save} disabled={busy || !dirty} size="md">{busy ? "Speichert…" : dirty ? "💾 Änderungen speichern" : "Gespeichert ✓"}</Btn>
            <span className="text-[11px] text-gray-600">{apps.length} von {APP_CATALOG.length} Apps freigegeben</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   PANEL 2: USER-MANAGEMENT
   ───────────────────────────────────────────────────────────────────────────── */
function UserMgmtPanel({ userRole }) {
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState("");
  const [roleFilter, setRole] = useState("");
  const [toast, setToast]     = useState(null);
  const [deactivating, setDeactivating] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (roleFilter) params.set("role", roleFilter);
      if (search)     params.set("search", search);
      const data = await apiFetch(`/admin/users?${params}`);
      setUsers(data.users || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter]);

  useEffect(() => { load(); }, [load]);

  const deactivate = async (userId, name) => {
    if (!confirm(`Benutzer "${name}" wirklich deaktivieren?`)) return;
    setDeactivating(userId);
    try {
      await apiFetch(`/admin/users/${userId}/deactivate`, { method: "PATCH" });
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, isActive: false } : u));
      setToast({ msg: `${name} deaktiviert`, type: "success" });
    } catch (e) {
      setToast({ msg: e.message, type: "error" });
    } finally {
      setDeactivating(null);
    }
  };

  const toggleVerify = async (userId, current, name) => {
    try {
      await apiFetch(`/admin/users/${userId}/verify`, { method: "POST", body: JSON.stringify({ verified: !current }) });
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, isVerified: !current } : u));
      setToast({ msg: !current ? `${name} verifiziert ✓` : `Verifizierung von ${name} entfernt`, type: "success" });
    } catch (e) {
      setToast({ msg: e.message, type: "error" });
    }
  };

  const ROLE_COLORS = {
    solid_ground: "#fbbf24", admin: "#c084fc", teacher: "#06b6d4", student: "#a78bfa",
    mentor: "#34d399", parent: "#fb923c",
  };

  const ROLES = [
    { value: "", label: "Alle Rollen" },
    { value: "admin", label: "Admin" },
    { value: "teacher", label: "Lehrer" },
    { value: "student", label: "Schüler" },
    { value: "mentor", label: "Mentor" },
    { value: "parent", label: "Elternteil" },
  ];

  const displayed = users;

  return (
    <div className="space-y-5">
      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}

      <div>
        <h2 className="text-xl font-black text-white mb-1">Nutzerverwaltung</h2>
        <p className="text-sm text-gray-600">Alle registrierten Nutzer — suche, filtere und verwalte Zugänge.</p>
      </div>

      <div className="flex gap-3 flex-wrap">
        <div className="flex-1 min-w-[180px]">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name suchen…"
            icon="🔍"
          />
        </div>
        <div className="w-40">
          <Select
            value={roleFilter}
            onChange={(e) => setRole(e.target.value)}
            options={ROLES}
            placeholder="Alle Rollen"
          />
        </div>
      </div>

      {loading ? <Spinner /> : (
        <>
          <p className="text-[10px] font-bold text-gray-700 uppercase tracking-widest">{displayed.length} Nutzer</p>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
            {displayed.map((u) => {
              const color = ROLE_COLORS[u.role] || "#8b5cf6";
              const lastLogin = u.lastLoginAt ? new Date(u.lastLoginAt) : null;
              const daysSince = lastLogin ? Math.floor((Date.now() - lastLogin) / 86400000) : null;
              const activityColor = daysSince === null ? "#6b7280" : daysSince < 3 ? "#34d399" : daysSince < 14 ? "#fbbf24" : "#f87171";
              return (
                <div key={u.id}
                  className="flex items-center gap-3 px-4 py-3 rounded-2xl border transition-all"
                  style={{
                    background: u.isActive ? "rgba(255,255,255,0.03)" : "rgba(239,68,68,0.04)",
                    borderColor: u.isActive ? "rgba(255,255,255,0.07)" : "rgba(239,68,68,0.2)",
                    opacity: u.isActive ? 1 : 0.55,
                  }}>
                  {/* Avatar */}
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center text-lg flex-shrink-0"
                    style={{ background: `${color}15` }}>
                    {u.avatar || "🦊"}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="text-sm font-black text-white truncate">{u.name}</p>
                      {u.isVerified && <VerifiedBadge role={u.role} size={14} />}
                      <Pill color={color}>{u.role}</Pill>
                      {!u.isActive && <Pill color="#ef4444">Deaktiviert</Pill>}
                    </div>
                    <p className="text-[10px] text-gray-600 mt-0.5">
                      {u.plz && `PLZ ${u.plz} · `}
                      {u.studentProfile && `XP: ${u.studentProfile.xp} · Streak: ${u.studentProfile.streak} · `}
                      {lastLogin ? (
                        <span style={{ color: activityColor }}>
                          {daysSince === 0 ? "Heute aktiv" : daysSince === 1 ? "Gestern aktiv" : `Vor ${daysSince} Tagen aktiv`}
                        </span>
                      ) : <span className="text-gray-700">Nie eingeloggt</span>}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-1.5 flex-shrink-0">
                    {u.role !== "student" && (userRole === "admin" || userRole === "solid_ground") && (
                      <button
                        onClick={() => toggleVerify(u.id, u.isVerified, u.name)}
                        title={u.isVerified ? "Verifizierung entfernen" : "Verifizieren"}
                        className="w-8 h-8 rounded-xl flex items-center justify-center transition-all hover:scale-110"
                        style={{ background: u.isVerified ? "rgba(34,197,94,0.15)" : "rgba(255,255,255,0.06)", border: `1px solid ${u.isVerified ? "rgba(34,197,94,0.3)" : "rgba(255,255,255,0.08)"}` }}
                      >
                        {u.isVerified ? "✓" : "·"}
                      </button>
                    )}
                    {u.isActive && userRole === "admin" && (
                      <Btn variant="danger" size="sm" disabled={deactivating === u.id} onClick={() => deactivate(u.id, u.name)}>
                        {deactivating === u.id ? "…" : "Deaktiv."}
                      </Btn>
                    )}
                  </div>
                </div>
              );
            })}
            {displayed.length === 0 && (
              <div className="text-center py-12 text-gray-700">Keine Nutzer gefunden</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   PANEL 4: INSTITUTIONEN (admin only)
   ───────────────────────────────────────────────────────────────────────────── */
function InstitutionenPanel({ institutions, onRefresh }) {
  const [showForm, setShowForm]   = useState(false);
  const [editInst, setEditInst]   = useState(null);
  const [form, setForm]           = useState({ name: "", type: "school", city: "", address: "", superAdminId: "", directorName: "", emergencyPhone: "" });
  const [loading, setLoading]     = useState(false);
  const [toast, setToast]         = useState(null);
  const [search, setSearch]       = useState("");
  const [admins, setAdmins]       = useState([]);

  useEffect(() => {
    apiFetch("/admin/users?role=admin").then((d) => setAdmins(d.users || [])).catch(() => {});
  }, []);

  const TYPES = [
    { value: "school",  label: "Schule" },
    { value: "center",  label: "Nachhilfezentrum" },
    { value: "youth",   label: "Jugendzentrum" },
    { value: "therapy", label: "Therapiezentrum" },
  ];

  const TYPE_META = {
    school:  { icon: "🏫", color: "#8b5cf6" },
    center:  { icon: "📚", color: "#06b6d4" },
    youth:   { icon: "🌱", color: "#10b981" },
    therapy: { icon: "💚", color: "#34d399" },
  };

  const EMPTY = { name: "", type: "school", city: "", address: "", superAdminId: "", directorName: "", emergencyPhone: "" };
  const openCreate = () => { setEditInst(null); setForm(EMPTY); setShowForm(true); };
  const openEdit   = (inst) => {
    setEditInst(inst);
    setForm({ name: inst.name, type: inst.type, city: inst.city || "", address: inst.address || "", superAdminId: inst.superAdminId || "", directorName: inst.directorName || "", emergencyPhone: inst.emergencyPhone || "" });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim()) return;
    setLoading(true);
    try {
      if (editInst) {
        await apiFetch(`/institutions/${editInst.id}`, { method: "PUT", body: JSON.stringify(form) });
        setToast({ msg: "Institution aktualisiert!", type: "success" });
      } else {
        await apiFetch("/institutions", { method: "POST", body: JSON.stringify(form) });
        setToast({ msg: "Institution erstellt!", type: "success" });
      }
      setShowForm(false);
      onRefresh();
    } catch (e) {
      setToast({ msg: e.message, type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const deactivate = async (id, name) => {
    if (!confirm(`Institution "${name}" wirklich deaktivieren?`)) return;
    try {
      await apiFetch(`/institutions/${id}`, { method: "DELETE" });
      setToast({ msg: `${name} deaktiviert`, type: "success" });
      onRefresh();
    } catch (e) {
      setToast({ msg: e.message, type: "error" });
    }
  };

  const displayed = institutions.filter((i) => !search || i.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-5">
      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-black text-white mb-1">Institutionen</h2>
          <p className="text-sm text-gray-600">Schulen, Nachhilfeinstitute, Jugendzentren und mehr verwalten.</p>
        </div>
        <Btn onClick={openCreate}>+ Neu erstellen</Btn>
      </div>

      <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Institution suchen…" icon="🔍" />

      {/* Create/Edit form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(16px)" }}
          onClick={() => setShowForm(false)}>
          <div className="w-full max-w-lg rounded-3xl border p-6 space-y-4 max-h-[90vh] overflow-y-auto"
            style={{ background: "#0d0d1e", borderColor: "rgba(139,92,246,0.35)",
              boxShadow: "0 0 60px rgba(139,92,246,0.15)", animation: "nd-in 0.35s cubic-bezier(.34,1.56,.64,1) both" }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-black text-white">{editInst ? "Institution bearbeiten" : "Neue Institution"}</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-600 hover:text-gray-400 text-xl">✕</button>
            </div>
            <Input label="Name *" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="z.B. GRG 5 Rainergasse" icon="🏫" />
            <Select label="Typ" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} options={TYPES} />
            <Input label="Stadt" value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} placeholder="z.B. Wien" icon="📍" />
            <Input label="Adresse" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} placeholder="z.B. Rainergasse 39" icon="🗺️" />
            <Input label="Schulleitung / Direktor" value={form.directorName} onChange={(e) => setForm((f) => ({ ...f, directorName: e.target.value }))} placeholder="z.B. Mag. Maria Müller" icon="👔" />
            <Input label="Notfall-Telefon" value={form.emergencyPhone} onChange={(e) => setForm((f) => ({ ...f, emergencyPhone: e.target.value }))} placeholder="z.B. +43 1 234 5678" icon="🚨" />
            {/* Super-Admin dropdown */}
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1.5 ml-1">Zuständiger Admin *</label>
              <select value={form.superAdminId} onChange={(e) => setForm((f) => ({ ...f, superAdminId: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-xl text-sm text-white outline-none appearance-none"
                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(139,92,246,0.18)" }}>
                <option value="">Keinen Admin zuweisen…</option>
                {admins.map((a) => (
                  <option key={a.id} value={a.id}>{a.name} ({a.role})</option>
                ))}
              </select>
            </div>
            <div className="flex gap-3 pt-2">
              <Btn onClick={save} disabled={loading || !form.name.trim()} size="lg" className="flex-1">
                {loading ? "Speichern…" : editInst ? "Speichern" : "Erstellen"}
              </Btn>
              <Btn variant="ghost" onClick={() => setShowForm(false)} size="lg">Abbrechen</Btn>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {displayed.map((inst) => {
          const meta = TYPE_META[inst.type] || TYPE_META.school;
          return (
            <div key={inst.id}
              className="flex items-center gap-3 px-4 py-3 rounded-2xl border"
              style={{ background: `${meta.color}06`, borderColor: `${meta.color}20` }}>
              <div className="text-2xl w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0"
                style={{ background: `${meta.color}15` }}>
                {meta.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-black text-white">{inst.name}</p>
                <p className="text-[10px] text-gray-600">
                  {TYPES.find((t) => t.value === inst.type)?.label || inst.type}
                  {inst.city && ` · ${inst.city}`}
                  {inst.directorName && ` · ${inst.directorName}`}
                </p>
                {inst.superAdmin && (
                  <p className="text-[10px] mt-0.5" style={{ color: "#c084fc" }}>
                    ⚙️ {inst.superAdmin.name}
                  </p>
                )}
                {inst.emergencyPhone && (
                  <p className="text-[10px]" style={{ color: "#f87171" }}>🚨 {inst.emergencyPhone}</p>
                )}
              </div>
              <div className="flex gap-2">
                <Btn variant="ghost" size="sm" onClick={() => openEdit(inst)}>✏️</Btn>
                <Btn variant="danger" size="sm" onClick={() => deactivate(inst.id, inst.name)}>Deaktiv.</Btn>
              </div>
            </div>
          );
        })}
        {displayed.length === 0 && (
          <div className="text-center py-12 text-gray-700">
            {search ? "Keine Treffer" : "Noch keine Institutionen — erstelle die erste!"}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   PANEL 6: KLASSEN-BUILDER
   ───────────────────────────────────────────────────────────────────────────── */
function KlassenPanel({ institutions, userRole }) {
  const [classes, setClasses]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null); // full class with members
  const [showForm, setShowForm]   = useState(false);
  const [editCls, setEditCls]     = useState(null);
  const [form, setForm]           = useState({ name: "", grade: "", emoji: "📚", color: "#8b5cf6", description: "", institutionId: "" });
  const [saving, setSaving]       = useState(false);
  const [toast, setToast]         = useState(null);

  // Add member
  const [showAddMember, setShowAddMember] = useState(false);
  const [allStudents, setAllStudents]     = useState([]);
  const [studentSearch, setStudentSearch] = useState("");
  const [addingMember, setAddingMember]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch("/classes");
      setClasses(data.classes || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openClass = async (cls) => {
    try {
      const data = await apiFetch(`/classes/${cls.id}`);
      setSelected(data.class);
    } catch (e) { setToast({ msg: e.message, type: "error" }); }
  };

  const openCreate = () => {
    setEditCls(null);
    setForm({ name: "", grade: "", emoji: "📚", color: "#8b5cf6", description: "", institutionId: "" });
    setShowForm(true);
  };

  const openEdit = (cls) => {
    setEditCls(cls);
    setForm({ name: cls.name, grade: cls.grade || "", emoji: cls.emoji, color: cls.color, description: cls.description || "", institutionId: cls.institutionId || "" });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editCls) {
        await apiFetch(`/classes/${editCls.id}`, { method: "PUT", body: JSON.stringify(form) });
        setToast({ msg: "Klasse aktualisiert!", type: "success" });
      } else {
        await apiFetch("/classes", { method: "POST", body: JSON.stringify(form) });
        setToast({ msg: "Klasse erstellt!", type: "success" });
      }
      setShowForm(false);
      load();
    } catch (e) { setToast({ msg: e.message, type: "error" }); }
    finally { setSaving(false); }
  };

  const deleteClass = async (id, name) => {
    if (!confirm(`Klasse "${name}" wirklich löschen?`)) return;
    try {
      await apiFetch(`/classes/${id}`, { method: "DELETE" });
      setToast({ msg: `${name} gelöscht`, type: "success" });
      if (selected?.id === id) setSelected(null);
      load();
    } catch (e) { setToast({ msg: e.message, type: "error" }); }
  };

  const openAddMember = async () => {
    try {
      const data = await apiFetch("/students");
      setAllStudents(data.students || []);
      setStudentSearch("");
      setShowAddMember(true);
    } catch (e) { setToast({ msg: e.message, type: "error" }); }
  };

  const addMember = async (studentProfileId) => {
    if (!selected) return;
    setAddingMember(studentProfileId);
    try {
      await apiFetch(`/classes/${selected.id}/members`, { method: "POST", body: JSON.stringify({ studentProfileId }) });
      setToast({ msg: "Schüler hinzugefügt!", type: "success" });
      setShowAddMember(false);
      const data = await apiFetch(`/classes/${selected.id}`);
      setSelected(data.class);
    } catch (e) { setToast({ msg: e.message, type: "error" }); }
    finally { setAddingMember(null); }
  };

  const removeMember = async (studentProfileId, name) => {
    if (!selected || !confirm(`${name} aus der Klasse entfernen?`)) return;
    try {
      await apiFetch(`/classes/${selected.id}/members/${studentProfileId}`, { method: "DELETE" });
      setToast({ msg: `${name} entfernt`, type: "success" });
      const data = await apiFetch(`/classes/${selected.id}`);
      setSelected(data.class);
    } catch (e) { setToast({ msg: e.message, type: "error" }); }
  };

  const EMOJIS = ["📚", "🎓", "🏫", "⚡", "🌟", "🔬", "🎨", "🏆", "🌱", "🔥"];

  const filteredStudents = allStudents.filter((s) => {
    if (!studentSearch) return true;
    return s.user?.name?.toLowerCase().includes(studentSearch.toLowerCase());
  }).filter((s) => !selected?.members?.some((m) => m.studentProfileId === s.id));

  return (
    <div className="space-y-5">
      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}

      {/* Add member modal */}
      {showAddMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(16px)" }}
          onClick={() => setShowAddMember(false)}>
          <div className="w-full max-w-md rounded-3xl border p-5 space-y-4"
            style={{ background: "#0d0d1e", borderColor: "rgba(139,92,246,0.35)", boxShadow: "0 0 60px rgba(139,92,246,0.15)", animation: "nd-in 0.35s cubic-bezier(.34,1.56,.64,1) both" }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-black text-white">Schüler hinzufügen</h3>
              <button onClick={() => setShowAddMember(false)} className="text-gray-600 hover:text-gray-400 text-xl">✕</button>
            </div>
            <Input value={studentSearch} onChange={(e) => setStudentSearch(e.target.value)} placeholder="Schüler suchen…" icon="🔍" />
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {filteredStudents.map((s) => (
                <div key={s.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer hover:scale-[1.01] transition-transform"
                  style={{ background: "rgba(139,92,246,0.05)", borderColor: "rgba(139,92,246,0.15)" }}>
                  <span className="text-lg">{s.user?.avatar || "🦊"}</span>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-white">{s.user?.name}</p>
                    <p className="text-[10px] text-gray-600">XP: {s.xp} · {s.grade || "Kein Jahrgang"}</p>
                  </div>
                  <Btn size="sm" disabled={addingMember === s.id} onClick={() => addMember(s.id)}>
                    {addingMember === s.id ? "…" : "+ Hinzufügen"}
                  </Btn>
                </div>
              ))}
              {filteredStudents.length === 0 && (
                <p className="text-center py-6 text-gray-700 text-sm">Keine passenden Schüler gefunden</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(16px)" }}
          onClick={() => setShowForm(false)}>
          <div className="w-full max-w-md rounded-3xl border p-5 space-y-4"
            style={{ background: "#0d0d1e", borderColor: "rgba(139,92,246,0.35)", boxShadow: "0 0 60px rgba(139,92,246,0.15)", animation: "nd-in 0.35s cubic-bezier(.34,1.56,.64,1) both" }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-black text-white">{editCls ? "Klasse bearbeiten" : "Neue Klasse"}</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-600 hover:text-gray-400 text-xl">✕</button>
            </div>

            {/* Emoji picker */}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-2 ml-1">Symbol</p>
              <div className="flex gap-2 flex-wrap">
                {EMOJIS.map((e) => (
                  <button key={e} onClick={() => setForm((f) => ({ ...f, emoji: e }))}
                    className="w-10 h-10 rounded-xl text-xl flex items-center justify-center transition-all"
                    style={{
                      background: form.emoji === e ? `${form.color}25` : "rgba(255,255,255,0.04)",
                      border: `1.5px solid ${form.emoji === e ? form.color : "rgba(255,255,255,0.08)"}`,
                      transform: form.emoji === e ? "scale(1.15)" : "scale(1)",
                    }}>
                    {e}
                  </button>
                ))}
              </div>
            </div>

            {/* Color picker */}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-2 ml-1">Farbe</p>
              <div className="flex gap-2">
                {["#8b5cf6","#06b6d4","#10b981","#f97316","#ef4444","#ec4899","#eab308","#3b82f6"].map((c) => (
                  <button key={c} onClick={() => setForm((f) => ({ ...f, color: c }))}
                    className="w-8 h-8 rounded-full transition-transform"
                    style={{
                      background: c,
                      transform: form.color === c ? "scale(1.2)" : "scale(1)",
                      boxShadow: form.color === c ? `0 0 10px ${c}` : "none",
                      border: form.color === c ? `2px solid white` : "2px solid transparent",
                    }} />
                ))}
              </div>
            </div>

            <Input label="Klassenname *" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="z.B. Mathe Gruppe A" />
            <Input label="Jahrgang / Stufe" value={form.grade} onChange={(e) => setForm((f) => ({ ...f, grade: e.target.value }))} placeholder="z.B. 8. Klasse" />
            <Input label="Beschreibung" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Optional…" />
            <Select label="Institution" value={form.institutionId} onChange={(e) => setForm((f) => ({ ...f, institutionId: e.target.value }))}
              options={institutions.map((i) => ({ value: i.id, label: i.name }))} placeholder="Keine Institution" />

            <div className="flex gap-3 pt-1">
              <Btn onClick={save} disabled={saving || !form.name.trim()} size="lg" className="flex-1">
                {saving ? "Speichern…" : editCls ? "Speichern" : "Erstellen"}
              </Btn>
              <Btn variant="ghost" onClick={() => setShowForm(false)} size="lg">Abbrechen</Btn>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-black text-white mb-1">Klassen-Builder</h2>
          <p className="text-sm text-gray-600">Erstelle Lerngruppen, weise Schüler zu und vergleiche den Fortschritt.</p>
        </div>
        <Btn onClick={openCreate}>+ Neue Klasse</Btn>
      </div>

      {loading ? <Spinner /> : (
        <div className="flex gap-5 flex-col lg:flex-row">
          {/* Class list */}
          <div className="w-full lg:w-72 space-y-2 flex-shrink-0">
            {classes.length === 0 && (
              <div className="text-center py-12 text-gray-700">
                <p className="text-3xl mb-3">📚</p>
                <p className="text-sm">Noch keine Klassen — erstelle die erste!</p>
              </div>
            )}
            {classes.map((cls) => (
              <div key={cls.id}
                className="flex items-center gap-3 px-4 py-3 rounded-2xl border cursor-pointer transition-all hover:scale-[1.01]"
                style={{
                  background: selected?.id === cls.id ? `${cls.color}15` : `${cls.color}06`,
                  borderColor: selected?.id === cls.id ? `${cls.color}50` : `${cls.color}20`,
                  boxShadow: selected?.id === cls.id ? `0 0 20px ${cls.color}20` : "none",
                }}
                onClick={() => openClass(cls)}>
                <div className="text-2xl w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0"
                  style={{ background: `${cls.color}18` }}>
                  {cls.emoji}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-black text-white truncate">{cls.name}</p>
                  <p className="text-[10px] text-gray-600">
                    {cls.memberCount} Schüler{cls.grade ? ` · ${cls.grade}` : ""}
                    {cls.institution && ` · ${cls.institution.name}`}
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <Btn variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEdit(cls); }}>✏️</Btn>
                  <Btn variant="danger" size="sm" onClick={(e) => { e.stopPropagation(); deleteClass(cls.id, cls.name); }}>🗑️</Btn>
                </div>
              </div>
            ))}
          </div>

          {/* Class detail */}
          {selected ? (
            <div className="flex-1 space-y-5">
              {/* Header */}
              <div className="flex items-center gap-4">
                <div className="text-4xl w-16 h-16 flex items-center justify-center rounded-2xl"
                  style={{ background: `${selected.color}20`, boxShadow: `0 0 20px ${selected.color}20` }}>
                  {selected.emoji}
                </div>
                <div>
                  <h3 className="text-2xl font-black text-white">{selected.name}</h3>
                  <p className="text-sm text-gray-600">
                    {selected.grade && `${selected.grade} · `}
                    {selected.institution?.name && `${selected.institution.name} · `}
                    {selected.memberCount} Mitglieder
                  </p>
                  {selected.description && <p className="text-xs text-gray-600 mt-1">{selected.description}</p>}
                </div>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-3 gap-3">
                <StatCard label="Mitglieder"    value={selected.memberCount} icon="👩‍🎓" color={selected.color} />
                <StatCard label="Ø XP"          value={selected.avgXp}       icon="⚡"    color={selected.color} />
                <StatCard label="Gesamt-XP"     value={selected.totalXp}     icon="🏆"   color={selected.color} />
              </div>

              {/* Members */}
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-600">Mitglieder ({selected.members?.length || 0})</p>
                <Btn size="sm" onClick={openAddMember}>+ Schüler hinzufügen</Btn>
              </div>

              <div className="space-y-2 max-h-72 overflow-y-auto">
                {(selected.members || []).map((m) => {
                  const s = m.studentProfile;
                  const lastLogin = s?.user?.lastLoginAt ? new Date(s.user.lastLoginAt) : null;
                  const daysSince = lastLogin ? Math.floor((Date.now() - lastLogin) / 86400000) : null;
                  return (
                    <div key={m.id}
                      className="flex items-center gap-3 px-4 py-2.5 rounded-xl border"
                      style={{ background: `${selected.color}06`, borderColor: `${selected.color}15` }}>
                      <span className="text-lg">{s?.user?.avatar || "🦊"}</span>
                      <div className="flex-1">
                        <p className="text-sm font-bold text-white">{s?.user?.name}</p>
                        <p className="text-[10px] text-gray-600">
                          XP: {s?.xp} · Streak: {s?.streak}
                          {daysSince !== null && (
                            <span style={{ color: daysSince < 3 ? "#34d399" : daysSince < 14 ? "#fbbf24" : "#f87171" }}>
                              {" · "}
                              {daysSince === 0 ? "Heute aktiv" : daysSince === 1 ? "Gestern" : `${daysSince}d inaktiv`}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Pill color={selected.color}>{s?.xp || 0} XP</Pill>
                        <Btn variant="danger" size="sm" onClick={() => removeMember(s?.id, s?.user?.name)}>✕</Btn>
                      </div>
                    </div>
                  );
                })}
                {(selected.members?.length || 0) === 0 && (
                  <div className="text-center py-8 text-gray-700 text-sm">
                    Noch keine Mitglieder — füge Schüler hinzu!
                  </div>
                )}
              </div>

              {/* Leaderboard preview */}
              {(selected.members?.length || 0) > 1 && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-3">Klassen-Rangliste</p>
                  <div className="space-y-1.5">
                    {[...selected.members]
                      .sort((a, b) => b.studentProfile.xp - a.studentProfile.xp)
                      .map((m, idx) => {
                        const s = m.studentProfile;
                        const medals = ["🥇", "🥈", "🥉"];
                        return (
                          <div key={m.id}
                            className="flex items-center gap-3 px-3 py-2 rounded-xl"
                            style={{ background: idx < 3 ? `${selected.color}10` : "rgba(255,255,255,0.02)" }}>
                            <span className="w-6 text-center text-sm">{medals[idx] || `${idx + 1}.`}</span>
                            <span className="text-base">{s?.user?.avatar || "🦊"}</span>
                            <span className="text-sm font-bold text-white flex-1">{s?.user?.name}</span>
                            <span className="text-xs font-black" style={{ color: selected.color }}>{s?.xp} XP</span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-700">
              <div className="text-center">
                <p className="text-4xl mb-3">👈</p>
                <p className="text-sm">Klasse auswählen um Details zu sehen</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   PANEL 7: AUFGABEN-MANAGER
   ───────────────────────────────────────────────────────────────────────────── */
const TASK_TYPE_LABELS = { irl: "🌍 IRL", digital: "💻 Digital", quiz: "⚡ Quiz" };
const TASK_EMOJIS = ["📌","🎯","🏃","🌱","📸","🧪","🎨","🏋️","📚","🌍","🤝","🔧","🎮","💡","🎤","🧹"];

function AufgabenPanel() {
  const [tabAuf, setTabAuf] = useState("list"); // list | review
  const [tasks, setTasks]   = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectNote, setRejectNote] = useState("");
  const [toast, setToast] = useState(null);
  const [form, setForm] = useState({
    title: "", description: "", emoji: "📌", type: "digital",
    xpReward: 100, coinsReward: 50, difficulty: 1, requiresProof: false,
  });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, pRes] = await Promise.all([
        apiFetch("/tasks"),
        apiFetch("/admin/tasks/pending"),
      ]);
      setTasks(tRes.tasks || []);
      setPending(pRes.tasks || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const createTask = async () => {
    try {
      await apiFetch("/tasks", { method: "POST", body: JSON.stringify(form) });
      setToast({ msg: "Aufgabe erstellt! 📌", type: "success" });
      setShowForm(false);
      setForm({ title: "", description: "", emoji: "📌", type: "digital", xpReward: 100, coinsReward: 50, difficulty: 1, requiresProof: false });
      loadAll();
    } catch (e) { setToast({ msg: e.message, type: "error" }); }
  };

  const deleteTask = async (id) => {
    if (!confirm("Aufgabe wirklich löschen?")) return;
    try {
      await apiFetch(`/tasks/${id}`, { method: "DELETE" });
      setToast({ msg: "Aufgabe gelöscht", type: "success" });
      loadAll();
    } catch (e) { setToast({ msg: e.message, type: "error" }); }
  };

  const approveTask = async (id) => {
    try {
      await apiFetch(`/admin/tasks/${id}/approve`, { method: "POST" });
      setToast({ msg: "✅ Aufgabe genehmigt!", type: "success" });
      loadAll();
    } catch (e) { setToast({ msg: e.message, type: "error" }); }
  };

  const rejectTask = async (id) => {
    try {
      await apiFetch(`/admin/tasks/${id}/reject`, { method: "POST", body: JSON.stringify({ note: rejectNote }) });
      setToast({ msg: "❌ Aufgabe abgelehnt", type: "success" });
      setRejectModal(null);
      setRejectNote("");
      loadAll();
    } catch (e) { setToast({ msg: e.message, type: "error" }); }
  };

  return (
    <div className="space-y-5">
      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-black text-white mb-1">Aufgaben-Manager</h2>
          <p className="text-sm text-gray-600">Aufgaben erstellen, verwalten und Schüler-Einreichungen prüfen.</p>
        </div>
        <Btn onClick={() => setShowForm(true)}>+ Neue Aufgabe</Btn>
      </div>

      {/* Tab toggle */}
      <div className="flex gap-2">
        {[
          { id: "list",   label: `Aufgaben (${tasks.length})` },
          { id: "review", label: `Prüfung (${pending.length})`, badge: pending.length },
        ].map((t) => (
          <button key={t.id} onClick={() => setTabAuf(t.id)}
            className="px-4 py-2 rounded-xl text-sm font-bold transition-all relative"
            style={{
              background: tabAuf === t.id ? "rgba(139,92,246,0.2)" : "rgba(255,255,255,0.04)",
              border: `1px solid ${tabAuf === t.id ? "rgba(139,92,246,0.45)" : "rgba(255,255,255,0.08)"}`,
              color: tabAuf === t.id ? "#c084fc" : "#6b7280",
            }}>
            {t.label}
            {t.badge > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-[9px] font-black text-white flex items-center justify-center">
                {t.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Create form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(16px)" }}
          onClick={() => setShowForm(false)}>
          <div className="w-full max-w-lg rounded-3xl border p-6 space-y-4"
            style={{ background: "#0d0d1e", borderColor: "rgba(139,92,246,0.35)", boxShadow: "0 0 60px rgba(139,92,246,0.15)", animation: "nd-in 0.35s cubic-bezier(.34,1.56,.64,1) both" }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-black text-white">Neue Aufgabe</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-600 hover:text-gray-400 text-xl">✕</button>
            </div>
            {/* Emoji picker */}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-2 ml-1">Symbol</p>
              <div className="flex gap-2 flex-wrap">
                {TASK_EMOJIS.map((e) => (
                  <button key={e} onClick={() => setForm((f) => ({ ...f, emoji: e }))}
                    className="w-9 h-9 rounded-xl text-lg transition-all hover:scale-110"
                    style={{ background: form.emoji === e ? "rgba(139,92,246,0.3)" : "rgba(255,255,255,0.05)", border: `1px solid ${form.emoji === e ? "rgba(139,92,246,0.6)" : "transparent"}` }}>
                    {e}
                  </button>
                ))}
              </div>
            </div>
            <Input label="Titel *" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="z.B. 5km laufen" icon={form.emoji} />
            <Input label="Beschreibung" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Was soll gemacht werden?" />
            <Select label="Typ" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
              options={[{ value: "digital", label: "💻 Digital" }, { value: "irl", label: "🌍 IRL" }, { value: "quiz", label: "⚡ Quiz" }]} />
            <div className="grid grid-cols-3 gap-3">
              <Input label="XP-Belohnung" type="number" value={form.xpReward} onChange={(e) => setForm((f) => ({ ...f, xpReward: +e.target.value }))} icon="⚡" />
              <Input label="Coins" type="number" value={form.coinsReward} onChange={(e) => setForm((f) => ({ ...f, coinsReward: +e.target.value }))} icon="🪙" />
              <Input label="Schwierigkeit (1-5)" type="number" value={form.difficulty} onChange={(e) => setForm((f) => ({ ...f, difficulty: Math.min(5, Math.max(1, +e.target.value)) }))} icon="⭐" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <div onClick={() => setForm((f) => ({ ...f, requiresProof: !f.requiresProof }))}
                className="w-10 h-6 rounded-full transition-all relative"
                style={{ background: form.requiresProof ? "#8b5cf6" : "rgba(255,255,255,0.1)" }}>
                <div className="absolute top-1 w-4 h-4 rounded-full bg-white transition-all"
                  style={{ left: form.requiresProof ? "calc(100% - 1.25rem)" : "0.25rem" }} />
              </div>
              <span className="text-sm text-gray-400">Nachweis erforderlich</span>
            </label>
            <div className="flex gap-3 pt-2">
              <Btn onClick={createTask} disabled={!form.title.trim()} size="lg" className="flex-1">Erstellen</Btn>
              <Btn variant="ghost" onClick={() => setShowForm(false)} size="lg">Abbrechen</Btn>
            </div>
          </div>
        </div>
      )}

      {/* Reject modal */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(16px)" }}
          onClick={() => setRejectModal(null)}>
          <div className="w-full max-w-sm rounded-3xl border p-6 space-y-4"
            style={{ background: "#0d0d1e", borderColor: "rgba(239,68,68,0.35)", boxShadow: "0 0 60px rgba(239,68,68,0.1)" }}
            onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-black text-white">Ablehnen</h3>
            <Input label="Begründung" value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} placeholder="Optional: Warum?" icon="📝" />
            <div className="flex gap-3">
              <Btn variant="danger" onClick={() => rejectTask(rejectModal)} size="lg" className="flex-1">Ablehnen</Btn>
              <Btn variant="ghost" onClick={() => setRejectModal(null)} size="lg">Abbrechen</Btn>
            </div>
          </div>
        </div>
      )}

      {loading ? <Spinner /> : (
        <>
          {tabAuf === "list" && (
            <div className="space-y-2">
              {tasks.length === 0 ? (
                <div className="text-center py-12 text-gray-700">Noch keine Aufgaben — erstelle die erste!</div>
              ) : (
                tasks.map((t) => (
                  <div key={t.id} className="flex items-center gap-3 px-4 py-3 rounded-2xl border"
                    style={{ background: "rgba(139,92,246,0.04)", borderColor: "rgba(139,92,246,0.15)" }}>
                    <span className="text-2xl w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0"
                      style={{ background: "rgba(139,92,246,0.1)" }}>{t.emoji}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-black text-white">{t.title}</p>
                      <p className="text-[10px] text-gray-600">
                        {TASK_TYPE_LABELS[t.type] || t.type} · ⚡ {t.xpReward} XP · 🪙 {t.coinsReward}
                        {t.requiresProof && " · 📸 Nachweis"}
                      </p>
                    </div>
                    <Btn variant="danger" size="sm" onClick={() => deleteTask(t.id)}>🗑️</Btn>
                  </div>
                ))
              )}
            </div>
          )}

          {tabAuf === "review" && (
            <div className="space-y-2">
              {pending.length === 0 ? (
                <div className="text-center py-12 text-gray-700">Keine offenen Einreichungen — alles geprüft! ✅</div>
              ) : (
                pending.map((sub) => (
                  <div key={sub.id} className="px-4 py-3 rounded-2xl border space-y-2"
                    style={{ background: "rgba(251,191,36,0.04)", borderColor: "rgba(251,191,36,0.2)" }}>
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">{sub.task?.emoji || "📌"}</span>
                      <div className="flex-1">
                        <p className="text-sm font-black text-white">{sub.task?.title}</p>
                        <p className="text-[10px] text-gray-500">
                          von {sub.studentProfile?.user?.name || "Unbekannt"}
                          {sub.proofNote && <> · "{sub.proofNote}"</>}
                        </p>
                        {sub.proofUrl && (
                          <a href={sub.proofUrl} target="_blank" rel="noreferrer"
                            className="text-[10px] text-cyan-400 hover:underline mt-0.5 block">
                            📎 Nachweis ansehen
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Btn variant="success" size="sm" onClick={() => approveTask(sub.id)}>✅ Genehmigen</Btn>
                      <Btn variant="danger" size="sm" onClick={() => setRejectModal(sub.id)}>❌ Ablehnen</Btn>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   PANEL 8: MELDUNGEN
   ───────────────────────────────────────────────────────────────────────────── */
const REPORT_TYPE_META = {
  chat_abuse:      { label: "Chat-Missbrauch",  color: "#ef4444", bg: "rgba(239,68,68,0.1)",   emoji: "💬" },
  bullying:        { label: "Mobbing",           color: "#f97316", bg: "rgba(249,115,22,0.1)",  emoji: "⚠️" },
  violence:        { label: "Gewalt",            color: "#dc2626", bg: "rgba(220,38,38,0.1)",   emoji: "🚫" },
  bug:             { label: "Bug-Meldung",       color: "#06b6d4", bg: "rgba(6,182,212,0.1)",   emoji: "🐛" },
  removal_request: { label: "Entfernungsantrag", color: "#8b5cf6", bg: "rgba(139,92,246,0.1)",  emoji: "🗑️" },
  inappropriate:   { label: "Unangemessen",      color: "#f59e0b", bg: "rgba(245,158,11,0.1)",  emoji: "🔞" },
  other:           { label: "Sonstiges",         color: "#6b7280", bg: "rgba(107,114,128,0.1)", emoji: "📋" },
};
const REPORT_STATUS_META = {
  open:      { label: "Offen",      color: "#ef4444" },
  reviewing: { label: "In Prüfung", color: "#f97316" },
  resolved:  { label: "Gelöst",     color: "#22c55e" },
  dismissed: { label: "Abgewiesen", color: "#6b7280" },
};

function MeldungenPanel() {
  const [filterStatus, setFilterStatus] = useState("open");
  const [filterType,   setFilterType]   = useState("all");
  const [reports, setReports]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [selected, setSelected]         = useState(null);
  const [resolution, setResolution]     = useState("");
  const [toast, setToast]               = useState(null);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== "all") params.set("status", filterStatus);
      if (filterType   !== "all") params.set("type",   filterType);
      const data = await apiFetch(`/admin/reports?${params}`);
      setReports(data.reports || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filterStatus, filterType]);

  useEffect(() => { loadReports(); }, [loadReports]);

  const updateReport = async (id, status) => {
    try {
      await apiFetch(`/admin/reports/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ status, resolution }),
      });
      setToast({ msg: "Meldung aktualisiert", type: "success" });
      setSelected(null);
      setResolution("");
      loadReports();
    } catch (e) { setToast({ msg: e.message, type: "error" }); }
  };

  const openCount = reports.filter((r) => r.status === "open").length;

  return (
    <div className="space-y-5">
      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-black text-white mb-1">Meldungen</h2>
          <p className="text-sm text-gray-600">User-Berichte, Bug-Meldungen, Entfernungsanträge</p>
        </div>
        {openCount > 0 && (
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
            style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)" }}>
            <span className="w-1.5 h-1.5 rounded-full bg-red-400" style={{ animation: "pulse 2s infinite" }} />
            <span className="text-[10px] font-bold text-red-400">{openCount} offen</span>
          </div>
        )}
      </div>

      {/* Status filter */}
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {[
          { id: "all",       label: "Alle" },
          { id: "open",      label: "Offen" },
          { id: "reviewing", label: "In Prüfung" },
          { id: "resolved",  label: "Gelöst" },
          { id: "dismissed", label: "Abgewiesen" },
        ].map((s) => (
          <button key={s.id} onClick={() => setFilterStatus(s.id)}
            className="flex-shrink-0 px-3 py-1 rounded-xl text-xs font-bold border transition-all"
            style={{
              background: filterStatus === s.id ? "rgba(139,92,246,0.2)" : "rgba(255,255,255,0.04)",
              borderColor: filterStatus === s.id ? "rgba(139,92,246,0.45)" : "rgba(255,255,255,0.08)",
              color: filterStatus === s.id ? "#c084fc" : "#6b7280",
            }}>
            {s.label}
          </button>
        ))}
      </div>

      {/* Type filter */}
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {[{ id: "all", label: "Alle Typen" }, ...Object.entries(REPORT_TYPE_META).map(([id, m]) => ({ id, label: m.label }))].map((t) => (
          <button key={t.id} onClick={() => setFilterType(t.id)}
            className="flex-shrink-0 px-3 py-1 rounded-xl text-[10px] font-bold border transition-all"
            style={{
              background: filterType === t.id ? "rgba(249,115,22,0.15)" : "rgba(255,255,255,0.04)",
              borderColor: filterType === t.id ? "rgba(249,115,22,0.4)" : "rgba(255,255,255,0.08)",
              color: filterType === t.id ? "#fb923c" : "#6b7280",
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(16px)" }}
          onClick={() => setSelected(null)}>
          <div className="w-full max-w-md rounded-3xl border p-6 space-y-4"
            style={{ background: "#0d0d1e", borderColor: "rgba(139,92,246,0.35)", boxShadow: "0 0 60px rgba(139,92,246,0.15)" }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-2xl">{REPORT_TYPE_META[selected.type]?.emoji || "📋"}</span>
                <div>
                  <p className="text-sm font-black text-white">{REPORT_TYPE_META[selected.type]?.label || selected.type}</p>
                  <p className="text-[10px] text-gray-600">
                    von {selected.reporter?.name || "Unbekannt"}
                    {selected.reportedUser && ` · gegen ${selected.reportedUser.name}`}
                  </p>
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="text-gray-600 hover:text-gray-400 text-xl">✕</button>
            </div>
            {selected.description && (
              <div className="px-4 py-3 rounded-xl text-sm text-gray-300"
                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                {selected.description}
              </div>
            )}
            <Input label="Notiz / Lösung" value={resolution} onChange={(e) => setResolution(e.target.value)}
              placeholder="Interne Notiz (optional)" icon="📝" />
            <div className="grid grid-cols-2 gap-2">
              <Btn variant="ghost" size="sm" onClick={() => updateReport(selected.id, "reviewing")}>🔍 In Prüfung</Btn>
              <Btn variant="success" size="sm" onClick={() => updateReport(selected.id, "resolved")}>✅ Gelöst</Btn>
              <Btn variant="danger" size="sm" onClick={() => updateReport(selected.id, "dismissed")}>🚫 Abweisen</Btn>
              <Btn variant="ghost" size="sm" onClick={() => setSelected(null)}>Schließen</Btn>
            </div>
          </div>
        </div>
      )}

      {loading ? <Spinner /> : (
        <div className="space-y-2">
          {reports.length === 0 ? (
            <div className="text-center py-12 text-gray-700">Keine Meldungen für diese Filter</div>
          ) : (
            reports.map((report) => {
              const tm = REPORT_TYPE_META[report.type]   || REPORT_TYPE_META.other;
              const sm = REPORT_STATUS_META[report.status] || REPORT_STATUS_META.open;
              return (
                <button key={report.id} onClick={() => { setSelected(report); setResolution(""); }}
                  className="w-full text-left flex items-start gap-3 p-4 rounded-2xl border transition-all hover:scale-[1.005] relative overflow-hidden"
                  style={{ background: `${tm.color}04`, borderColor: `${tm.color}20` }}>
                  <div className="absolute inset-y-0 left-0 w-0.5" style={{ background: tm.color }} />
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-lg"
                    style={{ background: tm.bg }}>{tm.emoji}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-[10px] font-black px-1.5 py-0.5 rounded"
                        style={{ color: tm.color, background: tm.bg }}>{tm.label}</span>
                      <span className="text-[10px] font-black" style={{ color: sm.color }}>{sm.label}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1 truncate">
                      {report.reporter?.name || "Anonym"}
                      {report.reportedUser && ` → ${report.reportedUser.name}`}
                    </p>
                    {report.description && (
                      <p className="text-[10px] text-gray-600 mt-0.5 line-clamp-1">{report.description}</p>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   PANEL 9: SCHULÜBERSICHT (admin — shows assigned schools + messages)
   ───────────────────────────────────────────────────────────────────────────── */
function SchulübersichtPanel({ currentUser }) {
  const [schools, setSchools]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [adminProfile, setAdminProfile] = useState({ isPublic: currentUser?.isPublic ?? true, maxSchools: currentUser?.maxSchools ?? 3, showSchoolCount: currentUser?.showSchoolCount ?? false, emergencyPhone: currentUser?.emergencyPhone || "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [toast, setToast] = useState(null);

  const loadSchools = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch("/admin/my-schools");
      setSchools(data.schools || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSchools(); }, [loadSchools]);

  const openSchool = async (school) => {
    setSelected(school);
    setMsgLoading(true);
    try {
      const data = await apiFetch(`/admin/my-schools/${school.id}/messages`);
      setMessages(data.messages || []);
    } catch (e) { console.error(e); }
    finally { setMsgLoading(false); }
  };

  const markRead = async (msgId) => {
    try {
      await apiFetch(`/admin/messages/${msgId}/read`, { method: "PATCH" });
      setMessages((prev) => prev.map((m) => m.id === msgId ? { ...m, isRead: true } : m));
    } catch (e) { console.error(e); }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await apiFetch("/users/me/admin-profile", { method: "PATCH", body: JSON.stringify(adminProfile) });
      setToast({ msg: "Profil gespeichert!", type: "success" });
    } catch (e) { setToast({ msg: e.message, type: "error" }); }
    finally { setSavingProfile(false); }
  };

  const totalSchools    = schools.length;
  const totalUnread     = schools.reduce((a, s) => a + (s.unreadMessages || 0), 0);
  const totalStudents   = schools.reduce((a, s) => a + (s.studentCount  || 0), 0);
  const totalTeachers   = schools.reduce((a, s) => a + (s.users?.length || 0), 0);

  return (
    <div className="space-y-6">
      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-black text-white mb-1">Meine Schulen</h2>
          <p className="text-sm text-gray-600">Institutionen unter deiner Verwaltung — Nachrichten, Lehrkräfte, Schüler.</p>
        </div>
        {totalUnread > 0 && (
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
            style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)" }}>
            <span className="w-1.5 h-1.5 rounded-full bg-red-400" style={{ animation: "pulse 2s infinite" }} />
            <span className="text-[10px] font-bold text-red-400">{totalUnread} neue Nachricht{totalUnread > 1 ? "en" : ""}</span>
          </div>
        )}
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Schulen" value={totalSchools} icon="🏫" color="#8b5cf6" sub={`von max. ${adminProfile.maxSchools}`} />
        <StatCard label="Schüler" value={totalStudents} icon="👩‍🎓" color="#06b6d4" />
        <StatCard label="Lehrer" value={totalTeachers} icon="👨‍🏫" color="#10b981" />
        <StatCard label="Ungelesen" value={totalUnread} icon="✉️" color="#ef4444" />
      </div>

      {/* Admin profile settings */}
      <Card accent="#8b5cf6">
        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-4">Admin-Profil Einstellungen</p>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-bold text-white">Profil öffentlich</p>
              <p className="text-[10px] text-gray-600">Dein Admin-Profil ist für Nutzer sichtbar</p>
            </div>
            <div onClick={() => setAdminProfile((p) => ({ ...p, isPublic: !p.isPublic }))}
              className="w-10 h-6 rounded-full transition-all relative cursor-pointer"
              style={{ background: adminProfile.isPublic ? "#8b5cf6" : "rgba(255,255,255,0.1)" }}>
              <div className="absolute top-1 w-4 h-4 rounded-full bg-white transition-all"
                style={{ left: adminProfile.isPublic ? "calc(100% - 1.25rem)" : "0.25rem" }} />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-bold text-white">Schulanzahl als Badge</p>
              <p className="text-[10px] text-gray-600">Öffentlich zeigen wie viele Schulen du verwaltest</p>
            </div>
            <div onClick={() => setAdminProfile((p) => ({ ...p, showSchoolCount: !p.showSchoolCount }))}
              className="w-10 h-6 rounded-full transition-all relative cursor-pointer"
              style={{ background: adminProfile.showSchoolCount ? "#8b5cf6" : "rgba(255,255,255,0.1)" }}>
              <div className="absolute top-1 w-4 h-4 rounded-full bg-white transition-all"
                style={{ left: adminProfile.showSchoolCount ? "calc(100% - 1.25rem)" : "0.25rem" }} />
            </div>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-2 ml-1">Max. Schulen (Slider)</p>
            <div className="flex items-center gap-3">
              <input type="range" min={1} max={50} value={adminProfile.maxSchools}
                onChange={(e) => setAdminProfile((p) => ({ ...p, maxSchools: +e.target.value }))}
                className="flex-1 accent-violet-500" />
              <span className="text-white font-black text-sm w-8 text-right">{adminProfile.maxSchools}</span>
              <input type="number" min={1} max={50} value={adminProfile.maxSchools}
                onChange={(e) => setAdminProfile((p) => ({ ...p, maxSchools: Math.min(50, Math.max(1, +e.target.value)) }))}
                className="w-16 px-2 py-1 rounded-xl text-sm text-white text-center"
                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(139,92,246,0.2)" }} />
            </div>
          </div>
          <Input label="Notfall-Telefon (für Lehrer sichtbar)" value={adminProfile.emergencyPhone}
            onChange={(e) => setAdminProfile((p) => ({ ...p, emergencyPhone: e.target.value }))}
            placeholder="z.B. +43 664 123 4567" icon="🚨" />
          <Btn onClick={saveProfile} disabled={savingProfile} className="w-full justify-center">
            {savingProfile ? "Speichern…" : "Profil speichern"}
          </Btn>
        </div>
      </Card>

      {/* School list + message detail */}
      {loading ? <Spinner /> : schools.length === 0 ? (
        <div className="text-center py-12 text-gray-700">
          <p className="text-4xl mb-3">🏫</p>
          <p className="text-sm">Dir wurden noch keine Schulen zugewiesen.<br/>Ein Solid Ground Admin muss dich als Super-Admin einer Institution eintragen.</p>
        </div>
      ) : (
        <div className={`grid gap-4 ${selected ? "lg:grid-cols-2" : "grid-cols-1"}`}>
          {/* School cards */}
          <div className="space-y-3">
            {schools.map((s) => (
              <button key={s.id} onClick={() => openSchool(s)}
                className="w-full text-left p-4 rounded-2xl border transition-all hover:scale-[1.01]"
                style={{
                  background: selected?.id === s.id ? "rgba(139,92,246,0.12)" : "rgba(255,255,255,0.03)",
                  borderColor: selected?.id === s.id ? "rgba(139,92,246,0.4)" : "rgba(255,255,255,0.07)",
                }}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                    style={{ background: "rgba(139,92,246,0.15)" }}>🏫</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-black text-white">{s.name}</p>
                      {s.unreadMessages > 0 && (
                        <span className="px-1.5 py-0.5 rounded-full text-[9px] font-black bg-red-500 text-white">{s.unreadMessages}</span>
                      )}
                    </div>
                    <p className="text-[10px] text-gray-500">{s.city || ""} · {s._count?.users || 0} Lehrer · {s.studentCount || 0} Schüler</p>
                    {s.directorName && <p className="text-[10px] text-gray-600 mt-0.5">👔 {s.directorName}</p>}
                    {s.emergencyPhone && <p className="text-[10px] mt-0.5" style={{ color: "#f87171" }}>🚨 {s.emergencyPhone}</p>}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Message detail */}
          {selected && (
            <div className="rounded-2xl border p-4 space-y-3"
              style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(139,92,246,0.2)" }}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-black text-white">{selected.name}</p>
                <button onClick={() => setSelected(null)} className="text-gray-600 hover:text-gray-400">✕</button>
              </div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-600">Direktionsnachrichten</p>
              {msgLoading ? <Spinner /> : messages.length === 0 ? (
                <p className="text-sm text-gray-700 text-center py-4">Keine Nachrichten</p>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {messages.map((m) => (
                    <div key={m.id} className="p-3 rounded-xl border cursor-pointer"
                      onClick={() => !m.isRead && markRead(m.id)}
                      style={{
                        background: m.isRead ? "rgba(255,255,255,0.02)" : "rgba(139,92,246,0.08)",
                        borderColor: m.priority === "urgent" ? "rgba(239,68,68,0.3)" : "rgba(255,255,255,0.07)",
                      }}>
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-xs font-black text-white">{m.subject}</p>
                        {m.priority === "urgent" && <span className="text-[9px] font-black text-red-400 flex-shrink-0">🚨 DRINGEND</span>}
                        {!m.isRead && <span className="w-2 h-2 rounded-full bg-violet-400 flex-shrink-0 mt-0.5" />}
                      </div>
                      <p className="text-[10px] text-gray-500 mt-0.5">{m.fromName}{m.fromEmail && ` · ${m.fromEmail}`}</p>
                      <p className="text-[10px] text-gray-400 mt-1 line-clamp-2">{m.body}</p>
                      <p className="text-[9px] text-gray-700 mt-1">{new Date(m.createdAt).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   PANEL 10: SOLID GROUND  (platform owner only — role: solid_ground)
   ───────────────────────────────────────────────────────────────────────────── */
function SolidGroundPanel() {
  const [tab, setTab] = useState("overview"); // overview | admins | users | codes | verify | messages
  const [stats, setStats]       = useState(null);
  const [admins, setAdmins]     = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [sgMessages, setSgMessages] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [toast, setToast]       = useState(null);
  const [newAdminName, setNewAdminName] = useState("");
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [newCode, setNewCode]   = useState(null);
  const [verifySearch, setVerifySearch] = useState("");
  const [togglingVerify, setTogglingVerify] = useState(null);
  // User management
  const [sgUsers, setSgUsers]           = useState([]);
  const [sgUserTotal, setSgUserTotal]   = useState(0);
  const [sgUserSearch, setSgUserSearch] = useState("");
  const [sgUserRoleF, setSgUserRoleF]   = useState("");
  const [sgUserPage, setSgUserPage]     = useState(1);
  const [loadingSgUsers, setLoadingSgUsers] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  // Code generation
  const [codeRole, setCodeRole]             = useState("student");
  const [codeCount, setCodeCount]           = useState(5);
  const [genCodes, setGenCodes]             = useState([]);
  const [generatingCodes, setGeneratingCodes] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsData, adminsData] = await Promise.all([
        apiFetch("/solid-ground/system-stats"),
        apiFetch("/solid-ground/admins"),
      ]);
      setStats(statsData);
      setAdmins(adminsData.admins || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const data = await apiFetch("/admin/users");
      setAllUsers((data.users || []).filter((u) => u.role !== "student"));
    } catch (e) { console.error(e); }
  }, []);

  const loadMessages = useCallback(async () => {
    try {
      const data = await apiFetch("/solid-ground/messages");
      setSgMessages(data.messages || []);
    } catch (e) { console.error(e); }
  }, []);

  const loadSGUsers = useCallback(async () => {
    setLoadingSgUsers(true);
    try {
      const qs = new URLSearchParams({ page: sgUserPage, limit: 30, ...(sgUserSearch ? { search: sgUserSearch } : {}), ...(sgUserRoleF ? { role: sgUserRoleF } : {}) });
      const data = await apiFetch(`/solid-ground/users?${qs}`);
      setSgUsers(data.users || []);
      setSgUserTotal(data.total || 0);
    } catch (e) { setToast({ msg: "Fehler beim Laden der Nutzer", type: "error" }); }
    finally { setLoadingSgUsers(false); }
  }, [sgUserPage, sgUserSearch, sgUserRoleF]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    if (tab === "verify") loadUsers();
    if (tab === "messages") loadMessages();
    if (tab === "users") loadSGUsers();
  }, [tab, loadUsers, loadMessages, loadSGUsers]);
  // Re-load users when filters/page change
  useEffect(() => { if (tab === "users") loadSGUsers(); }, [sgUserPage, sgUserSearch, sgUserRoleF]); // eslint-disable-line react-hooks/exhaustive-deps

  const createAdmin = async () => {
    if (!newAdminName.trim()) return;
    setCreatingAdmin(true);
    try {
      const data = await apiFetch("/solid-ground/admins", { method: "POST", body: JSON.stringify({ name: newAdminName }) });
      setNewCode(data.code);
      setNewAdminName("");
      setToast({ msg: data.message, type: "success" });
      loadData();
    } catch (e) { setToast({ msg: e.message, type: "error" }); }
    finally { setCreatingAdmin(false); }
  };

  const toggleAdminActive = async (id, current, name) => {
    try {
      await apiFetch(`/solid-ground/admins/${id}`, { method: "PATCH", body: JSON.stringify({ isActive: !current }) });
      setAdmins((prev) => prev.map((a) => a.id === id ? { ...a, isActive: !current } : a));
      setToast({ msg: !current ? `${name} reaktiviert` : `${name} deaktiviert`, type: "success" });
    } catch (e) { setToast({ msg: e.message, type: "error" }); }
  };

  const toggleVerify = async (userId, current, name) => {
    setTogglingVerify(userId);
    try {
      await apiFetch(`/solid-ground/verify/${userId}`, { method: "POST", body: JSON.stringify({ verified: !current }) });
      setAllUsers((prev) => prev.map((u) => u.id === userId ? { ...u, isVerified: !current } : u));
      setToast({ msg: !current ? `✓ ${name} verifiziert` : `${name} nicht mehr verifiziert`, type: "success" });
    } catch (e) { setToast({ msg: e.message, type: "error" }); }
    finally { setTogglingVerify(null); }
  };

  const generateAccessCodes = async () => {
    setGeneratingCodes(true);
    try {
      const data = await apiFetch("/solid-ground/access-codes", {
        method: "POST",
        body: JSON.stringify({ count: codeCount, role: codeRole }),
      });
      setGenCodes(data.codes || []);
      setToast({ msg: `${data.codes.length} Codes generiert!`, type: "success" });
    } catch (e) { setToast({ msg: e.message, type: "error" }); }
    finally { setGeneratingCodes(false); }
  };

  const deleteUser = async (id, name) => {
    if (!confirm(`"${name}" permanent löschen? Nicht rückgängig machbar!`)) return;
    setDeletingUser(id);
    try {
      await apiFetch(`/solid-ground/users/${id}`, { method: "DELETE" });
      setSgUsers((p) => p.filter((u) => u.id !== id));
      setSgUserTotal((t) => t - 1);
      setToast({ msg: `${name} gelöscht`, type: "success" });
    } catch (e) { setToast({ msg: e.message, type: "error" }); }
    finally { setDeletingUser(null); }
  };

  const SG_TABS = [
    { id: "overview",  label: "System",      icon: "🌐" },
    { id: "admins",    label: "Admins",      icon: "👤" },
    { id: "users",     label: "Alle Nutzer", icon: "👥" },
    { id: "codes",     label: "Codes",       icon: "🔑" },
    { id: "verify",    label: "Verifizierung", icon: "✓" },
    { id: "messages",  label: "Nachrichten", icon: "✉️" },
  ];

  const filteredUsers = allUsers.filter((u) => !verifySearch || u.name.toLowerCase().includes(verifySearch.toLowerCase()));

  const SG_ROLE_COLORS = { solid_ground: "#fbbf24", admin: "#c084fc", teacher: "#06b6d4", mentor: "#34d399", parent: "#fb923c" };

  return (
    <div className="space-y-5">
      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="relative">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center"
            style={{ background: "linear-gradient(135deg, #fbbf24, #f59e0b)", boxShadow: "0 0 24px rgba(251,191,36,0.4)" }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
              <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
            </svg>
          </div>
          <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-green-400 border-2"
            style={{ borderColor: "#06060f", boxShadow: "0 0 6px #4ade80" }} />
        </div>
        <div>
          <h2 className="text-xl font-black text-white flex items-center gap-2">
            Solid Ground
            <VerifiedBadge role="solid_ground" size={18} />
          </h2>
          <p className="text-sm text-gray-600">Platform Owner Control Center — nur für dich sichtbar.</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {SG_TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold transition-all"
            style={{
              background: tab === t.id ? "rgba(251,191,36,0.15)" : "rgba(255,255,255,0.04)",
              border: `1px solid ${tab === t.id ? "rgba(251,191,36,0.45)" : "rgba(255,255,255,0.08)"}`,
              color: tab === t.id ? "#fbbf24" : "#6b7280",
            }}>
            <span>{t.icon}</span>{t.label}
          </button>
        ))}
      </div>

      {loading && tab === "overview" ? <Spinner /> : (
        <>
          {/* System Overview */}
          {tab === "overview" && stats && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <StatCard label="Nutzer gesamt" value={stats.stats.totalUsers} icon="👥" color="#fbbf24" />
                <StatCard label="Schüler" value={stats.stats.students} icon="👩‍🎓" color="#8b5cf6" />
                <StatCard label="Lehrer" value={stats.stats.teachers} icon="👨‍🏫" color="#06b6d4" />
                <StatCard label="Admins" value={stats.stats.admins} icon="⚙️" color="#c084fc" />
                <StatCard label="Institutionen" value={stats.stats.totalInstitutions} icon="🏫" color="#10b981" />
                <StatCard label="Offene Meldungen" value={stats.stats.openReports} icon="⚠️" color="#ef4444" />
              </div>
              {stats.recentUsers?.length > 0 && (
                <Card accent="#fbbf24">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-3">Zuletzt registriert</p>
                  <div className="space-y-2">
                    {stats.recentUsers.map((u) => (
                      <div key={u.id} className="flex items-center gap-3">
                        <span className="text-base">{u.avatar || "🦊"}</span>
                        <div className="flex-1">
                          <div className="flex items-center gap-1.5">
                            <p className="text-xs font-black text-white">{u.name}</p>
                            {u.isVerified && <VerifiedBadge role={u.role} size={12} />}
                          </div>
                          <p className="text-[10px] text-gray-600">{u.role}</p>
                        </div>
                        <p className="text-[9px] text-gray-700">{new Date(u.createdAt).toLocaleDateString("de-DE")}</p>
                        {!u.isActive && <Pill color="#ef4444">Inaktiv</Pill>}
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* Admin Management */}
          {tab === "admins" && (
            <div className="space-y-4">
              {/* New admin code */}
              <Card accent="#fbbf24">
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-3">Neuen Admin-Registrierungscode erstellen</p>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <Input value={newAdminName} onChange={(e) => setNewAdminName(e.target.value)}
                      placeholder="Name des Admins (zur internen Referenz)" icon="👤" />
                  </div>
                  <Btn onClick={createAdmin} disabled={creatingAdmin || !newAdminName.trim()}>
                    {creatingAdmin ? "…" : "Erstellen"}
                  </Btn>
                </div>
                {newCode && (
                  <div className="mt-3 p-3 rounded-xl border flex items-center justify-between"
                    style={{ background: "rgba(251,191,36,0.08)", borderColor: "rgba(251,191,36,0.3)" }}>
                    <div>
                      <p className="text-[10px] font-bold text-yellow-400 mb-1">Code (7 Tage gültig)</p>
                      <p className="font-mono text-lg font-black tracking-widest text-white">{newCode}</p>
                    </div>
                    <button onClick={() => { navigator.clipboard?.writeText(newCode); setToast({ msg: "Code kopiert!", type: "success" }); }}
                      className="text-yellow-400 hover:text-yellow-300 text-xl transition-colors">📋</button>
                  </div>
                )}
              </Card>

              {/* Admin list */}
              <div className="space-y-2">
                {admins.map((a) => (
                  <div key={a.id} className="p-4 rounded-2xl border transition-all"
                    style={{ background: a.role === "solid_ground" ? "rgba(251,191,36,0.06)" : "rgba(255,255,255,0.03)", borderColor: a.role === "solid_ground" ? "rgba(251,191,36,0.25)" : "rgba(255,255,255,0.07)", opacity: a.isActive ? 1 : 0.55 }}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center text-lg flex-shrink-0"
                        style={{ background: a.role === "solid_ground" ? "rgba(251,191,36,0.15)" : "rgba(192,132,252,0.15)" }}>
                        {a.avatar || "👤"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-black text-white">{a.name}</p>
                          {a.isVerified && <VerifiedBadge role={a.role} size={14} />}
                          <Pill color={SG_ROLE_COLORS[a.role] || "#8b5cf6"}>{a.role}</Pill>
                          {a.isPublic && <Pill color="#10b981">Öffentlich</Pill>}
                        </div>
                        <p className="text-[10px] text-gray-600 mt-0.5">
                          {a.superAdminOf?.length > 0 && `${a.superAdminOf.length} Schule${a.superAdminOf.length > 1 ? "n" : ""}`}
                          {a.emergencyPhone && ` · 🚨 ${a.emergencyPhone}`}
                          {a.lastLoginAt && ` · Letzte Anmeldung: ${new Date(a.lastLoginAt).toLocaleDateString("de-DE")}`}
                        </p>
                        {a.superAdminOf?.length > 0 && (
                          <p className="text-[9px] text-gray-700 mt-0.5">
                            {a.superAdminOf.map((s) => s.name).join(" · ")}
                          </p>
                        )}
                      </div>
                      {a.role !== "solid_ground" && (
                        <Btn variant={a.isActive ? "danger" : "success"} size="sm"
                          onClick={() => toggleAdminActive(a.id, a.isActive, a.name)}>
                          {a.isActive ? "Deaktiv." : "Reaktiv."}
                        </Btn>
                      )}
                    </div>
                  </div>
                ))}
                {admins.length === 0 && <p className="text-center py-8 text-gray-700">Noch keine Admins — erstelle den ersten!</p>}
              </div>
            </div>
          )}

          {/* Verification management */}
          {tab === "verify" && (
            <div className="space-y-4">
              <Card accent="#22c55e">
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-2">Verifizierungssystem</p>
                <p className="text-xs text-gray-500">Verifizierte Nutzer erhalten ein blaues Häkchen-Badge. Vergib es an echte Lehrer, Mentoren und Professionals — niemals an Schüler. Solid Ground Admin hat immer ein goldenes Badge.</p>
              </Card>
              <Input value={verifySearch} onChange={(e) => setVerifySearch(e.target.value)} placeholder="Nutzer suchen…" icon="🔍" />
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {filteredUsers.map((u) => (
                  <div key={u.id} className="flex items-center gap-3 px-4 py-3 rounded-2xl border transition-all"
                    style={{ background: u.isVerified ? "rgba(34,197,94,0.04)" : "rgba(255,255,255,0.02)", borderColor: u.isVerified ? "rgba(34,197,94,0.2)" : "rgba(255,255,255,0.07)" }}>
                    <span className="text-base">{u.avatar || "🦊"}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-black text-white truncate">{u.name}</p>
                        {u.isVerified && <VerifiedBadge role={u.role} size={14} />}
                      </div>
                      <p className="text-[10px] text-gray-600">
                        <span style={{ color: SG_ROLE_COLORS[u.role] || "#8b5cf6" }}>{u.role}</span>
                        {u.institutionId && " · Hat Institution"}
                      </p>
                    </div>
                    <button
                      disabled={togglingVerify === u.id}
                      onClick={() => toggleVerify(u.id, u.isVerified, u.name)}
                      className="px-3 py-1.5 rounded-xl text-xs font-bold transition-all hover:scale-105"
                      style={{
                        background: u.isVerified ? "rgba(34,197,94,0.15)" : "rgba(59,130,246,0.15)",
                        border: `1px solid ${u.isVerified ? "rgba(34,197,94,0.3)" : "rgba(59,130,246,0.3)"}`,
                        color: u.isVerified ? "#4ade80" : "#60a5fa",
                      }}>
                      {togglingVerify === u.id ? "…" : u.isVerified ? "✓ Verifiziert" : "Verifizieren"}
                    </button>
                  </div>
                ))}
                {filteredUsers.length === 0 && <p className="text-center py-8 text-gray-700">Keine Nutzer gefunden</p>}
              </div>
            </div>
          )}

          {/* All users management */}
          {tab === "users" && (
            <div className="space-y-4">
              <div className="flex gap-2">
                <div className="flex-1">
                  <Input value={sgUserSearch} onChange={(e) => { setSgUserSearch(e.target.value); setSgUserPage(1); }} placeholder="Nutzer suchen…" icon="🔍" />
                </div>
                <select value={sgUserRoleF} onChange={(e) => { setSgUserRoleF(e.target.value); setSgUserPage(1); }}
                  className="px-3 rounded-xl text-xs font-bold"
                  style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", color: "#9ca3af" }}>
                  <option value="">Alle Rollen</option>
                  {["student","teacher","admin","mentor","parent"].map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
              {loadingSgUsers ? <Spinner /> : (
                <>
                  <p className="text-[10px] text-gray-600">{sgUserTotal} Nutzer gesamt</p>
                  <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
                    {sgUsers.map((u) => (
                      <div key={u.id} className="flex items-center gap-3 px-4 py-3 rounded-2xl border"
                        style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.07)", opacity: u.isActive ? 1 : 0.45 }}>
                        <span className="text-base flex-shrink-0">{u.avatar || "🦊"}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="text-xs font-black text-white truncate">{u.name}</p>
                            {u.isVerified && <VerifiedBadge role={u.role} size={12} />}
                            {!u.isActive && <Pill color="#ef4444">Gesperrt</Pill>}
                          </div>
                          <p className="text-[10px] mt-0.5" style={{ color: SG_ROLE_COLORS[u.role] || "#8b5cf6" }}>
                            {u.role}{u.institution?.name && ` · ${u.institution.name}`}
                          </p>
                        </div>
                        <div className="flex gap-1.5 flex-shrink-0">
                          <Btn variant={u.isActive ? "danger" : "success"} size="sm"
                            onClick={async () => {
                              try {
                                await apiFetch(`/solid-ground/users/${u.id}`, { method: "PATCH", body: JSON.stringify({ isActive: !u.isActive }) });
                                setSgUsers((p) => p.map((x) => x.id === u.id ? { ...x, isActive: !u.isActive } : x));
                                setToast({ msg: u.isActive ? `${u.name} gesperrt` : `${u.name} reaktiviert`, type: "success" });
                              } catch (e) { setToast({ msg: e.message, type: "error" }); }
                            }}>
                            {u.isActive ? "Sperren" : "Aktiv."}
                          </Btn>
                          <button disabled={deletingUser === u.id} onClick={() => deleteUser(u.id, u.name)}
                            className="p-1.5 rounded-lg transition-all"
                            style={{ color: "rgba(239,68,68,0.5)" }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = "#f87171"; e.currentTarget.style.background = "rgba(239,68,68,0.1)"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(239,68,68,0.5)"; e.currentTarget.style.background = "transparent"; }}
                            title="Permanent löschen">
                            {deletingUser === u.id ? "…" : "🗑"}
                          </button>
                        </div>
                      </div>
                    ))}
                    {sgUsers.length === 0 && !loadingSgUsers && (
                      <p className="text-center py-8 text-gray-700">Keine Nutzer gefunden</p>
                    )}
                  </div>
                  {sgUserTotal > 30 && (
                    <div className="flex justify-center items-center gap-3">
                      <Btn variant="secondary" size="sm" disabled={sgUserPage <= 1} onClick={() => setSgUserPage((p) => p - 1)}>←</Btn>
                      <span className="text-xs text-gray-600">{sgUserPage} / {Math.ceil(sgUserTotal / 30)}</span>
                      <Btn variant="secondary" size="sm" disabled={sgUserPage >= Math.ceil(sgUserTotal / 30)} onClick={() => setSgUserPage((p) => p + 1)}>→</Btn>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Access code generator */}
          {tab === "codes" && (
            <div className="space-y-4">
              <Card accent="#fbbf24">
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-3">Zugangscodes generieren</p>
                <div className="flex flex-wrap gap-5">
                  <div>
                    <p className="text-[10px] text-gray-600 font-bold uppercase tracking-widest mb-2">Für Rolle</p>
                    <div className="flex gap-1.5">
                      {[["student","Schüler"],["teacher","Lehrer"],["mentor","Mentor"]].map(([v, l]) => (
                        <button key={v} onClick={() => setCodeRole(v)}
                          className="px-3 py-1.5 rounded-xl text-xs font-bold transition-all"
                          style={{
                            background: codeRole === v ? "rgba(251,191,36,0.2)" : "rgba(255,255,255,0.05)",
                            border: `1px solid ${codeRole === v ? "rgba(251,191,36,0.5)" : "rgba(255,255,255,0.08)"}`,
                            color: codeRole === v ? "#fbbf24" : "#6b7280",
                          }}>
                          {l}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-600 font-bold uppercase tracking-widest mb-2">Anzahl</p>
                    <div className="flex gap-1.5">
                      {[1, 5, 10, 20, 50].map((n) => (
                        <button key={n} onClick={() => setCodeCount(n)}
                          className="w-10 py-1.5 rounded-xl text-xs font-bold transition-all"
                          style={{
                            background: codeCount === n ? "rgba(251,191,36,0.2)" : "rgba(255,255,255,0.05)",
                            border: `1px solid ${codeCount === n ? "rgba(251,191,36,0.5)" : "rgba(255,255,255,0.08)"}`,
                            color: codeCount === n ? "#fbbf24" : "#6b7280",
                          }}>
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-end">
                    <Btn onClick={generateAccessCodes} disabled={generatingCodes}>
                      {generatingCodes ? "Generiere…" : `${codeCount}× Erstellen`}
                    </Btn>
                  </div>
                </div>
              </Card>
              {genCodes.length > 0 && (
                <Card accent="#10b981">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-600">{genCodes.length} Codes für {codeRole}</p>
                    <button
                      onClick={() => { navigator.clipboard?.writeText(genCodes.join("\n")); setToast({ msg: "Alle kopiert!", type: "success" }); }}
                      className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors">
                      📋 Alle kopieren
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {genCodes.map((c, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-2 rounded-xl"
                        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(16,185,129,0.15)" }}>
                        <span className="font-mono text-sm font-black text-white tracking-widest">{c}</span>
                        <button onClick={() => { navigator.clipboard?.writeText(c); setToast({ msg: "Kopiert!", type: "success" }); }}
                          className="text-gray-600 hover:text-yellow-400 transition-colors text-sm">📋</button>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* Institution messages */}
          {tab === "messages" && (
            <div className="space-y-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-600">{sgMessages.length} Nachrichten</p>
              {sgMessages.length === 0 ? (
                <p className="text-center py-12 text-gray-700">Keine Nachrichten vorhanden</p>
              ) : (
                sgMessages.map((m) => (
                  <div key={m.id} className="p-4 rounded-2xl border"
                    style={{ background: m.isRead ? "rgba(255,255,255,0.02)" : "rgba(139,92,246,0.07)", borderColor: m.priority === "urgent" ? "rgba(239,68,68,0.3)" : "rgba(255,255,255,0.08)" }}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-black text-white">{m.subject}</p>
                        <p className="text-[10px] text-gray-500 mt-0.5">
                          {m.institution?.name} · {m.fromName}
                          {m.fromEmail && ` · ${m.fromEmail}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {m.priority === "urgent" && <Pill color="#ef4444">🚨 DRINGEND</Pill>}
                        {!m.isRead && <span className="w-2 h-2 rounded-full bg-violet-400" />}
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-2 line-clamp-2">{m.body}</p>
                    <p className="text-[9px] text-gray-700 mt-2">{new Date(m.createdAt).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   OVERVIEW PANEL (Stats)
   ───────────────────────────────────────────────────────────────────────────── */
function OverviewPanel() {
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch("/admin/stats")
      .then((data) => setStats(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;
  if (!stats) return <p className="text-gray-600 text-sm">Statistiken nicht verfügbar</p>;

  const s = stats.stats;
  const shadowColors = {
    "Sprössling":    "#34d399",
    "Flammenträger": "#fb923c",
    "Blitzdenker":   "#facc15",
    "Diamantkopf":   "#38bdf8",
    "Legende":       "#c084fc",
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-black text-white mb-1">Übersicht</h2>
        <p className="text-sm text-gray-600">Live-Statistiken aus deinem StudoX-Ökosystem.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatCard label="Schüler"   value={s.students}  icon="👩‍🎓" color="#8b5cf6" />
        <StatCard label="Lehrer"    value={s.teachers}  icon="👨‍🏫" color="#06b6d4" />
        <StatCard label="Mentoren"  value={s.mentors}   icon="🤝" color="#10b981" />
        <StatCard label="Eltern"    value={s.parents}   icon="👪" color="#f97316" />
        <StatCard label="Quiz-Versuche" value={s.attempts} icon="📝" color="#fbbf24" />
        <StatCard label="Ø XP"     value={s.avgXp}     icon="⚡" color="#c084fc" sub={`${s.coinsGiven} Coins vergeben`} />
      </div>

      {/* Shadow distribution */}
      <Card accent="#8b5cf6">
        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-4">Shadow-Verteilung</p>
        <div className="space-y-2.5">
          {Object.entries(s.shadowDist || {}).map(([name, count]) => {
            const total = s.students || 1;
            const pct = Math.round((count / total) * 100);
            const color = shadowColors[name] || "#8b5cf6";
            return (
              <div key={name}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-bold text-white">{name}</span>
                  <span style={{ color }} className="font-black">{count} ({pct}%)</span>
                </div>
                <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
                  <div className="h-full rounded-full transition-all duration-1000"
                    style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${color}80, ${color})` }} />
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Top students */}
      {stats.topStudents?.length > 0 && (
        <Card accent="#fbbf24">
          <p className="text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-3">Top Schüler</p>
          <div className="space-y-2">
            {stats.topStudents.map((s, i) => {
              const medals = ["🥇", "🥈", "🥉", "4.", "5."];
              return (
                <div key={s.id} className="flex items-center gap-3">
                  <span className="w-6 text-center text-sm">{medals[i]}</span>
                  <span className="text-lg">{s.user?.avatar || "🦊"}</span>
                  <span className="text-sm font-bold text-white flex-1">{s.user?.name}</span>
                  <Pill color="#fbbf24">{s.xp} XP</Pill>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Recent activity */}
      {stats.recentActivity?.length > 0 && (
        <Card accent="#06b6d4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-3">Letzte Aktivitäten</p>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {stats.recentActivity.map((a, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <span>{a.quiz?.emoji || "📝"}</span>
                <span className="text-gray-400 flex-1 truncate">{a.studentProfile?.user?.name} — {a.quiz?.title}</span>
                <Pill color="#06b6d4">{a.score}/{a.totalQuestions}</Pill>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   PANEL 11: SANKTIONEN — grouped by school → class, powerful filters + real
   user search (was a flat unstructured list in the old Kontrollzentrum).
   ───────────────────────────────────────────────────────────────────────────── */
const SANCTION_TYPES = [
  { id: "warning", label: "⚠️ Verwarnung" },
  { id: "mute", label: "🔇 Stumm" },
  { id: "content_removed", label: "🗑️ Inhalt entfernt" },
  { id: "ban", label: "🚫 Bann" },
];
const SANCTION_GLYPH = { warning: "⚠️", mute: "🔇", content_removed: "🗑️", ban: "🚫" };

function UserPicker({ value, onSelect }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      apiFetch(`/admin/users?search=${encodeURIComponent(q.trim())}`)
        .then((d) => setResults((d.users || []).slice(0, 8)))
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="relative">
      <input
        value={value ? `${value.name} (${value.role})` : q}
        onChange={(e) => { onSelect(null); setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Name suchen…"
        className="w-full px-3.5 py-2.5 rounded-xl text-sm text-white outline-none mb-1"
        style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.14)" }}
      />
      {open && results.length > 0 && !value && (
        <div className="absolute z-10 w-full mt-1 rounded-xl border overflow-hidden max-h-56 overflow-y-auto"
          style={{ background: "#0d0d1f", borderColor: "rgba(139,92,246,0.3)" }}>
          {results.map((u) => (
            <button key={u.id} onClick={() => { onSelect(u); setQ(""); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-violet-500/10 transition-colors">
              <span>{u.avatar || "🦊"}</span>
              <span className="flex-1 min-w-0 truncate text-white">{u.name}</span>
              <span className="text-[10px] text-gray-500 uppercase">{u.role}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SanctionsPanel() {
  const [list, setList] = useState(null);
  const [target, setTarget] = useState(null);
  const [type, setType] = useState("warning");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [statusFilter, setStatusFilter] = useState("active");
  const [search, setSearch] = useState("");

  const load = useCallback(() => {
    apiFetch("/sanctions").then((d) => setList(d.sanctions || [])).catch(() => setList([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const issue = async () => {
    if (!target || !reason.trim()) { setToast({ msg: "Nutzer und Grund nötig", type: "error" }); return; }
    setBusy(true);
    try {
      await apiFetch("/sanctions", { method: "POST", body: JSON.stringify({ userId: target.id, type, reason: reason.trim() }) });
      setToast({ msg: "Sanktion verhängt", type: "success" }); setTarget(null); setReason(""); load();
    } catch (e) { setToast({ msg: e.message || "Fehlgeschlagen", type: "error" }); }
    finally { setBusy(false); }
  };
  const lift = async (id) => {
    try { await apiFetch(`/sanctions/${id}/lift`, { method: "PATCH" }); setToast({ msg: "Aufgehoben", type: "success" }); load(); }
    catch { setToast({ msg: "Fehlgeschlagen", type: "error" }); }
  };

  const filtered = (list || []).filter((s) => {
    if (statusFilter !== "all" && s.status !== statusFilter) return false;
    if (search.trim()) {
      const hay = `${s.user?.name || ""} ${s.institution?.name || ""} ${s.classes?.map((c) => c.name).join(" ") || ""}`.toLowerCase();
      if (!hay.includes(search.trim().toLowerCase())) return false;
    }
    return true;
  });

  // group: institution → class → sanctions[]
  const groups = new Map();
  for (const s of filtered) {
    const instKey = s.institution?.name || "Ohne Schule";
    if (!groups.has(instKey)) groups.set(instKey, new Map());
    const classKey = s.classes?.[0]?.name || "Ohne Klasse";
    const clsMap = groups.get(instKey);
    if (!clsMap.has(classKey)) clsMap.set(classKey, []);
    clsMap.get(classKey).push(s);
  }

  return (
    <div className="space-y-6">
      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
      <div>
        <h2 className="text-xl font-black text-white mb-1">Sanktionen</h2>
        <p className="text-sm text-gray-600">Verwarnen, stummschalten oder bannen — übersichtlich nach Schule &amp; Klasse gruppiert.</p>
      </div>

      <Card>
        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-2 ml-1">Sanktion verhängen</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1.5 ml-1">Nutzer</label>
            <UserPicker value={target} onSelect={setTarget} />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1.5 ml-1">Art</label>
            <div className="flex flex-wrap gap-1.5">
              {SANCTION_TYPES.map((s) => (
                <button key={s.id} onClick={() => setType(s.id)} className="px-3 py-2 rounded-lg text-[12px] font-bold transition-all"
                  style={type === s.id ? { background: "linear-gradient(135deg,#f43f5e,#fb923c)", color: "#fff" } : { background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.6)", border: "1px solid rgba(255,255,255,0.12)" }}>{s.label}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-3">
          <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1.5 ml-1">Grund</label>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Warum? (wird bei einem Einspruch angezeigt)"
            className="w-full px-3.5 py-2.5 rounded-xl text-sm text-white outline-none resize-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.14)" }} />
        </div>
        <Btn onClick={issue} disabled={busy} className="mt-3">{busy ? "…" : "Sanktion verhängen"}</Btn>
      </Card>

      {/* filters */}
      <div className="flex flex-wrap items-center gap-2">
        {[["active", "Aktiv"], ["lifted", "Aufgehoben"], ["all", "Alle"]].map(([id, label]) => (
          <button key={id} onClick={() => setStatusFilter(id)} className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
            style={statusFilter === id ? { background: "linear-gradient(135deg,#8b5cf6,#06b6d4)", color: "#fff" } : { background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.55)", border: "1px solid rgba(255,255,255,0.1)" }}>{label}</button>
        ))}
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="🔎 Name, Schule oder Klasse…"
          className="flex-1 min-w-[160px] px-3 py-1.5 rounded-lg text-xs text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} />
        <span className="text-[11px] text-gray-600">{filtered.length} Einträge</span>
      </div>

      {list === null ? (
        <div className="text-center text-sm text-gray-500 py-8">Lade…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-sm text-gray-500 py-8">Keine Sanktionen gefunden.</div>
      ) : (
        <div className="space-y-5">
          {[...groups.entries()].map(([instName, clsMap]) => (
            <div key={instName}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm">🏛️</span>
                <span className="text-sm font-black text-violet-300">{instName}</span>
                <span className="text-[10px] text-gray-600">{[...clsMap.values()].reduce((a, c) => a + c.length, 0)} Einträge</span>
              </div>
              <div className="space-y-3 pl-4 border-l" style={{ borderColor: "rgba(139,92,246,0.15)" }}>
                {[...clsMap.entries()].map(([clsName, items]) => (
                  <div key={clsName}>
                    <p className="text-[11px] font-bold text-gray-500 mb-1.5">📚 {clsName}</p>
                    <div className="space-y-1.5">
                      {items.map((s) => (
                        <div key={s.id} className="flex items-center gap-2.5 p-2.5 rounded-xl" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
                          <span className="text-lg">{SANCTION_GLYPH[s.type] || "•"}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-[12.5px] font-bold text-white truncate">{s.user?.name || "Unbekannt"} <span className="text-gray-500 font-normal">— {s.reason}</span></div>
                            <div className="text-[10px] text-gray-500 truncate">{s.status}{s.appealStatus !== "none" ? ` · Einspruch: ${s.appealStatus}` : ""} · {new Date(s.createdAt).toLocaleDateString("de-DE")}</div>
                          </div>
                          {s.status === "active" && (
                            <button onClick={() => lift(s.id)} className="text-[11px] font-bold px-2.5 py-1 rounded-lg flex-shrink-0" style={{ background: "rgba(52,211,153,0.15)", color: "#34d399" }}>aufheben</button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   PANEL 12: AUDIT — filterable security log (actions, actor search, date range)
   ───────────────────────────────────────────────────────────────────────────── */
const AUDIT_ACTION_META = {
  "account.create":            { glyph: "👤", label: "Account erstellt",                color: "#34d399" },
  "code.create":               { glyph: "🎟️", label: "Einladung erstellt",              color: "#8b5cf6" },
  "auth.admin_login":          { glyph: "⚡", label: "SuperAdmin-Login",                color: "#06b6d4" },
  "auth.admin_login_failed":   { glyph: "⛔", label: "SuperAdmin-Login fehlgeschlagen", color: "#f43f5e" },
  "auth.overseer_login":       { glyph: "👁️", label: "Overseer-Login",                  color: "#f5d020" },
  "auth.overseer_login_failed":{ glyph: "⛔", label: "Overseer-Login fehlgeschlagen",   color: "#f43f5e" },
};
const auditMeta = (a) => AUDIT_ACTION_META[a] || { glyph: "•", label: a, color: "#9ca3af" };

function AuditPanel() {
  const [logs, setLogs] = useState(null);
  const [note, setNote] = useState("");
  const [q, setQ] = useState("");
  const [actionFilter, setActionFilter] = useState("all");

  useEffect(() => {
    let alive = true;
    apiFetch("/audit?limit=200")
      .then((d) => { if (alive) { setLogs(d.logs || []); setNote(d.note || ""); } })
      .catch(() => { if (alive) { setLogs([]); setNote("Audit-Log nicht verfügbar."); } });
    return () => { alive = false; };
  }, []);

  const actions = ["all", ...new Set((logs || []).map((l) => l.action))];
  const filtered = (logs || []).filter((l) => {
    if (actionFilter !== "all" && l.action !== actionFilter) return false;
    if (q.trim() && !`${l.actorName || ""} ${l.actorRole || ""}`.toLowerCase().includes(q.trim().toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-black text-white mb-1">Audit-Log</h2>
        <p className="text-sm text-gray-600">Jede sicherheitsrelevante Aktion im Ecosystem — durchsuchbar &amp; filterbar.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}
          className="px-3 py-1.5 rounded-lg text-xs text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }}>
          {actions.map((a) => <option key={a} value={a} style={{ background: "#0d0d1f" }}>{a === "all" ? "Alle Aktionen" : auditMeta(a).label}</option>)}
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="🔎 Akteur suchen…"
          className="flex-1 min-w-[160px] px-3 py-1.5 rounded-lg text-xs text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} />
        <span className="text-[11px] text-gray-600 self-center">{filtered.length} Einträge</span>
      </div>

      {logs === null ? (
        <div className="text-center text-sm text-gray-500 py-8">Lade Audit-Log…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-sm text-gray-400 py-8">
          <div className="text-3xl mb-2">🛡️</div>
          Keine Einträge.{note ? <div className="text-[11px] text-gray-600 mt-1">{note}</div> : null}
        </div>
      ) : (
        <div className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1">
          {filtered.map((l) => {
            const m = auditMeta(l.action);
            return (
              <div key={l.id} className="flex items-center gap-3 p-2.5 rounded-xl" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
                <span className="grid place-items-center rounded-lg flex-shrink-0" style={{ width: 30, height: 30, fontSize: 15, background: `${m.color}1f`, border: `1px solid ${m.color}44` }}>{m.glyph}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-bold text-white truncate">{m.label}</div>
                  <div className="text-[10.5px] text-gray-500 truncate">
                    {l.actorName || "—"}{l.actorRole ? ` · ${l.actorRole}` : ""}{l.meta ? ` · ${typeof l.meta === "string" ? l.meta : JSON.stringify(l.meta)}` : ""}
                  </div>
                </div>
                <span className="text-[10px] text-gray-600 flex-shrink-0 tabular-nums">{new Date(l.createdAt).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   PANEL 13: EINSPRÜCHE — the Overseer's highest-court review queue
   ───────────────────────────────────────────────────────────────────────────── */
function AppealsPanel() {
  const [list, setList] = useState(null);
  const [toast, setToast] = useState(null);
  const load = useCallback(() => {
    apiFetch("/sanctions?appealStatus=pending").then((d) => setList(d.sanctions || [])).catch(() => setList([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const review = async (id, decision) => {
    try {
      await apiFetch(`/sanctions/${id}/review`, { method: "POST", body: JSON.stringify({ decision }) });
      setToast({ msg: decision === "approved" ? "Einspruch stattgegeben" : "Einspruch abgelehnt", type: "success" }); load();
    } catch { setToast({ msg: "Fehlgeschlagen", type: "error" }); }
  };

  return (
    <div className="space-y-5">
      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
      <div>
        <h2 className="text-xl font-black text-white mb-1">Einsprüche</h2>
        <p className="text-sm text-gray-600">Du bist die letzte Instanz. Hier landen alle Einsprüche gegen Admin-Entscheidungen.</p>
      </div>
      {list === null ? (
        <div className="text-center text-sm text-gray-500 py-8">Lade…</div>
      ) : list.length === 0 ? (
        <div className="text-center text-sm text-gray-400 py-8"><div className="text-3xl mb-2">⚖️</div>Keine offenen Einsprüche.</div>
      ) : (
        <div className="space-y-2.5">
          {list.map((s) => (
            <Card key={s.id}>
              <div className="flex items-center gap-2"><span className="text-lg">{SANCTION_GLYPH[s.type] || "•"}</span><span className="text-sm font-bold text-white">{s.user?.name || s.type}</span><span className="text-[10px] text-gray-500">{s.institution?.name}</span></div>
              <div className="mt-1 text-[12px] text-gray-300"><b className="text-white/70">Grund:</b> {s.reason}</div>
              <div className="mt-1 text-[12px] text-gray-300"><b className="text-white/70">Einspruch:</b> {s.appealText || "—"}</div>
              <div className="flex gap-2 mt-3">
                <Btn size="sm" onClick={() => review(s.id, "approved")}>Stattgeben (aufheben)</Btn>
                <Btn size="sm" variant="ghost" onClick={() => review(s.id, "rejected")}>Ablehnen</Btn>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   MAIN DASHBOARD COMPONENT
   ───────────────────────────────────────────────────────────────────────────── */
const NAV_ITEMS = [
  { id: "overview",      label: "Übersicht",      icon: "📊", roles: ["solid_ground", "admin", "teacher"] },
  { id: "codes",         label: "Einladungen",    icon: "🎟️", roles: ["solid_ground", "admin", "teacher"] },
  { id: "users",         label: "Nutzer",         icon: "👥", roles: ["solid_ground", "admin", "teacher"] },
  { id: "aufgaben",      label: "Aufgaben",       icon: "📌", roles: ["solid_ground", "admin", "teacher"] },
  { id: "meldungen",     label: "Meldungen",      icon: "⚠️", roles: ["solid_ground", "admin"] },
  { id: "institutions",  label: "Institutionen",  icon: "🏛️", roles: ["solid_ground", "admin"] },
  { id: "schulen",       label: "Meine Schulen",  icon: "🏫", roles: ["solid_ground", "admin"] },
  { id: "classes",       label: "Klassen",        icon: "📚", roles: ["solid_ground", "admin", "teacher"] },
  { id: "studio",        label: "Creator Studio", icon: "🎬", roles: ["solid_ground", "admin", "teacher"] },
  { id: "sanctions",     label: "Sanktionen",     icon: "🚫", roles: ["solid_ground", "admin"] },
  { id: "audit",         label: "Audit",          icon: "🛡️", roles: ["solid_ground", "admin"] },
  { id: "appeals",       label: "Einsprüche",     icon: "⚖️", roles: ["solid_ground"] },
];

export default function CoreDashboard({ onClose }) {
  const { user, logout } = useAuthStore();
  const [tab, setTab]         = useState("overview");
  const [institutions, setInstitutions] = useState([]);
  const [navOpen, setNavOpen] = useState(false); // mobile nav

  const loadInstitutions = useCallback(async () => {
    try {
      const data = await fetch(`${API.replace("/api", "")}/api/institutions`).then((r) => r.json());
      setInstitutions(data.institutions || []);
    } catch {}
  }, []);

  useEffect(() => { loadInstitutions(); }, [loadInstitutions]);

  const visibleNav = NAV_ITEMS.filter((n) => n.roles.includes(user?.role));
  const activeNav  = visibleNav.find((n) => n.id === tab)
    || (tab === "solid_ground" ? { icon: "⭐", label: "Solid Ground" } : null)
    || visibleNav[0];

  return (
    <div
      className="fixed inset-0 z-[200] flex"
      style={{ background: "#06060f", color: "#fff" }}
    >
      {/* ── Sidebar ──────────────────────────────────────────────────────── */}
      {/* Mobile backdrop */}
      {navOpen && (
        <div className="fixed inset-0 z-[201] bg-black/60 lg:hidden" onClick={() => setNavOpen(false)} />
      )}

      <aside
        className={`
          fixed lg:relative inset-y-0 left-0 z-[202] w-64 flex-shrink-0 flex flex-col
          border-r transition-transform duration-300
          ${navOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
        style={{ borderColor: "rgba(139,92,246,0.15)", background: "#08081a" }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-5 border-b" style={{ borderColor: "rgba(139,92,246,0.12)" }}>
          <div style={{ filter: "drop-shadow(0 0 12px rgba(139,92,246,0.7))" }}>
            <svg width="32" height="32" viewBox="0 0 80 80" fill="none">
              <defs>
                <radialGradient id="cd-orb" cx="38%" cy="30%" r="65%">
                  <stop offset="0%"   stopColor="#f5f3ff"/>
                  <stop offset="40%"  stopColor="#a855f7"/>
                  <stop offset="100%" stopColor="#3b0764"/>
                </radialGradient>
                <radialGradient id="cd-na" cx="38%" cy="28%" r="65%">
                  <stop offset="0%"  stopColor="#cffafe"/>
                  <stop offset="100%" stopColor="#0e7490"/>
                </radialGradient>
                <radialGradient id="cd-nb" cx="38%" cy="28%" r="65%">
                  <stop offset="0%"  stopColor="#ede9fe"/>
                  <stop offset="100%" stopColor="#6d28d9"/>
                </radialGradient>
                <radialGradient id="cd-nc" cx="38%" cy="28%" r="65%">
                  <stop offset="0%"  stopColor="#d1fae5"/>
                  <stop offset="100%" stopColor="#065f46"/>
                </radialGradient>
                <linearGradient id="cd-r1" x1="0%" y1="50%" x2="100%" y2="50%">
                  <stop offset="0%"   stopColor="#8b5cf6" stopOpacity="0.15"/>
                  <stop offset="30%"  stopColor="#c084fc" stopOpacity="0.9"/>
                  <stop offset="70%"  stopColor="#06b6d4" stopOpacity="0.85"/>
                  <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.1"/>
                </linearGradient>
                <linearGradient id="cd-r2" x1="0%" y1="50%" x2="100%" y2="50%">
                  <stop offset="0%"   stopColor="#06b6d4" stopOpacity="0.15"/>
                  <stop offset="50%"  stopColor="#8b5cf6" stopOpacity="0.7"/>
                  <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.1"/>
                </linearGradient>
              </defs>
              <circle cx="60" cy="60" r="37" fill="none" stroke="rgba(139,92,246,0.1)" strokeWidth="0.8"/>
              <ellipse cx="60" cy="60" rx="37" ry="14" fill="none" stroke="url(#cd-r1)" strokeWidth="1.5" opacity="0.7"/>
              <ellipse cx="60" cy="60" rx="37" ry="14" fill="none" stroke="url(#cd-r2)" strokeWidth="1.2" opacity="0.55" transform="rotate(60 60 60)"/>
              <ellipse cx="60" cy="60" rx="37" ry="14" fill="none" stroke="rgba(192,132,252,0.45)" strokeWidth="1" transform="rotate(-60 60 60)"/>
              <line x1="60" y1="34" x2="60" y2="47" stroke="rgba(200,160,255,0.55)" strokeWidth="1.2" strokeDasharray="2.5 2"/>
              <line x1="85.5" y1="75.5" x2="72.5" y2="67.8" stroke="rgba(139,92,246,0.55)" strokeWidth="1.2" strokeDasharray="2.5 2"/>
              <line x1="34.5" y1="75.5" x2="47.5" y2="67.8" stroke="rgba(6,182,212,0.55)" strokeWidth="1.2" strokeDasharray="2.5 2"/>
              <circle cx="60" cy="29" r="6.5" fill="url(#cd-na)"/>
              <ellipse cx="58.5" cy="27.2" rx="3" ry="1.8" fill="white" opacity="0.55" transform="rotate(-15 58.5 27.2)"/>
              <circle cx="88" cy="77" r="5.5" fill="url(#cd-nb)"/>
              <ellipse cx="86.5" cy="75.5" rx="2.5" ry="1.5" fill="white" opacity="0.5" transform="rotate(-15 86.5 75.5)"/>
              <circle cx="32" cy="77" r="5" fill="url(#cd-nc)"/>
              <ellipse cx="30.5" cy="75.5" rx="2.2" ry="1.3" fill="white" opacity="0.45" transform="rotate(-15 30.5 75.5)"/>
              <circle cx="60" cy="60" r="13" fill="url(#cd-orb)"/>
              <circle cx="60" cy="60" r="13" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="0.8"/>
              <ellipse cx="56" cy="55" rx="5.5" ry="3.2" fill="white" opacity="0.28" transform="rotate(-20 56 55)"/>
              <circle cx="60" cy="60" r="4.5" fill="rgba(255,255,255,0.72)"/>
              <circle cx="60" cy="60" r="2" fill="white"/>
            </svg>
          </div>
          <div>
            <p className="text-sm font-black text-white">Admin Dashboard</p>
            <p className="text-[10px] text-gray-600">StudoX Core</p>
          </div>
        </div>

        {/* User chip */}
        <div className="mx-4 mt-4 px-3 py-2.5 rounded-xl border flex items-center gap-2"
          style={{ background: "rgba(139,92,246,0.07)", borderColor: "rgba(139,92,246,0.2)" }}>
          <span className="text-base">{user?.avatar || "🦊"}</span>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-black text-white truncate">{user?.name}</p>
            <p className="text-[9px] text-gray-600 uppercase tracking-widest">{user?.role}</p>
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-green-500" style={{ boxShadow: "0 0 4px #22c55e" }} />
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {visibleNav.map((item) => {
            const active = tab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => { setTab(item.id); setNavOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold transition-all"
                style={{
                  background: active ? "rgba(139,92,246,0.18)" : "transparent",
                  color: active ? "#c084fc" : "#6b7280",
                  borderLeft: active ? "3px solid #8b5cf6" : "3px solid transparent",
                }}
              >
                <span className="text-base">{item.icon}</span>
                {item.label}
              </button>
            );
          })}
        </nav>

        {/* Solid Ground special button — only for platform owner */}
        {user?.role === "solid_ground" && (
          <div className="px-3 pb-3">
            <button
              onClick={() => { setTab("solid_ground"); setNavOpen(false); }}
              className="w-full flex items-center gap-2.5 px-3 py-3 rounded-xl text-sm font-black transition-all hover:scale-[1.02] active:scale-[0.98]"
              style={{
                background: tab === "solid_ground"
                  ? "linear-gradient(135deg, rgba(251,191,36,0.25), rgba(245,158,11,0.15))"
                  : "linear-gradient(135deg, rgba(251,191,36,0.12), rgba(245,158,11,0.06))",
                border: `1px solid ${tab === "solid_ground" ? "rgba(251,191,36,0.6)" : "rgba(251,191,36,0.3)"}`,
                color: "#fbbf24",
                boxShadow: tab === "solid_ground"
                  ? "0 0 20px rgba(251,191,36,0.25), inset 0 0 12px rgba(251,191,36,0.06)"
                  : "0 0 12px rgba(251,191,36,0.12)",
              }}
            >
              <VerifiedBadge role="solid_ground" size={15} />
              <span>Solid Ground</span>
              <div className="ml-auto w-1.5 h-1.5 rounded-full"
                style={{ background: "#fbbf24", boxShadow: "0 0 6px #fbbf24", animation: "pulse 2s infinite" }} />
            </button>
          </div>
        )}

        {/* Close + Logout */}
        <div className="px-4 pb-5 space-y-1.5">
          <button
            onClick={onClose}
            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "#6b7280" }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
            Zurück zum Hub
          </button>
          <button
            onClick={() => { logout(); onClose(); }}
            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
            style={{ background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.18)", color: "#f87171" }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            Abmelden
          </button>
        </div>
      </aside>

      {/* ── Main content ──────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar (mobile) */}
        <div className="flex items-center gap-3 px-4 py-3 border-b lg:hidden"
          style={{ borderColor: "rgba(139,92,246,0.12)", background: "#08081a" }}>
          <button
            onClick={() => setNavOpen(true)}
            className="p-2 rounded-xl"
            style={{ background: "rgba(139,92,246,0.1)" }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#c084fc" strokeWidth="2.5" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>
          <p className="text-sm font-black text-white">{activeNav?.icon} {activeNav?.label}</p>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          {/* Top gradient line */}
          <div className="h-0.5 w-full" style={{ background: "linear-gradient(90deg, transparent, #8b5cf6, #06b6d4, transparent)" }} />

          <div className="max-w-4xl mx-auto px-5 py-8">
            {tab === "overview"      && <OverviewPanel />}
            {tab === "codes"         && <CodeGenPanel institutions={institutions} userRole={user?.role} />}
            {tab === "users"         && <UserMgmtPanel userRole={user?.role} />}
            {tab === "aufgaben"      && <AufgabenPanel />}
            {tab === "meldungen"     && (user?.role === "admin" || user?.role === "solid_ground") && <MeldungenPanel />}
            {tab === "institutions"  && (user?.role === "admin" || user?.role === "solid_ground") && (
              <InstitutionenPanel institutions={institutions} onRefresh={loadInstitutions} />
            )}
            {tab === "schulen"       && (user?.role === "admin" || user?.role === "solid_ground") && (
              <SchulübersichtPanel currentUser={user} />
            )}
            {tab === "classes"       && (
              <KlassenPanel institutions={institutions} userRole={user?.role} />
            )}
            {tab === "studio"        && (
              <CreatorStudioPanel currentUser={user} institutions={institutions} />
            )}
            {tab === "sanctions"     && (user?.role === "admin" || user?.role === "solid_ground") && (
              <SanctionsPanel />
            )}
            {tab === "audit"         && (user?.role === "admin" || user?.role === "solid_ground") && (
              <AuditPanel />
            )}
            {tab === "appeals"       && user?.role === "solid_ground" && (
              <AppealsPanel />
            )}
            {tab === "solid_ground"  && user?.role === "solid_ground" && (
              <SolidGroundPanel />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
